<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch, type CSSProperties } from 'vue'
import AppSelectField from '@/components/AppSelectField.vue'
import { GOODS_STATUS_OPTIONS } from '@/components/appSelectOptions'
import ImageOptionPickerField from '@/components/ImageOptionPickerField.vue'
import NumberStepperField from '@/components/NumberStepperField.vue'
import VariableIndexField from '@/components/eventEditors/VariableIndexField.vue'
import CanvasPreviewSurface, {
  type PreviewDrawItem,
  type PreviewHotspotItem,
} from '@/components/workspace/CanvasPreviewSurface.vue'
import { VARIABLE_OPERATOR_OPTIONS } from '@/features/event/variableBatch'
import { formatStoryMapId } from '@/features/story/storyTreeProjection'
import ResourceSelectField from '@/features/virtual-resource/components/ResourceSelectField.vue'
import { resolveResourceUrlFromFileName } from '@/features/virtual-resource/fileNamePath'
import EventID from '@/model/EventClass/EventID'
import { GoodsTypeEnum } from '@/model/MallNewClass/Goods'
import { useWorkspaceStore } from '@/store/workspace'
import type {
  SettingsButtonIndexDraft,
  SettingsFileDraft,
  SettingsMallFontDraft,
  SettingsMallPropertyDraft,
  SettingsPaymentDetailDraft,
  SettingsPaymentGoodsDraft,
  SettingsPaymentTabProps,
  SettingsViewportDraft,
} from '@/features/workspace/settings/types'

type PaymentDragTarget =
  | 'decorate'
  | 'area'
  | 'close'
  | 'prev'
  | 'next'
  | 'onekey'
  | 'page'
  | 'flower'
  | 'flower-own'
  | 'flower-price'
  | 'goods-back'
  | 'goods-icon'
  | 'goods-preview-back'
  | 'goods-name'
  | 'goods-intro'
  | 'goods-flower'
  | 'goods-price'
  | 'goods-status'

type PaymentResizeTarget = 'area' | 'goods-back' | 'goods-icon' | 'goods-preview-back' | 'goods-intro'

type DetailDragTarget =
  | 'preview-back'
  | 'preview'
  | 'name'
  | 'total'
  | 'flower'
  | 'price'
  | 'count'
  | 'intro'
  | 'ok'
  | 'cancel'
  | 'add'
  | 'minus'

type DetailResizeTarget = 'preview-back' | 'preview' | 'intro'
type SharedGoodsNumberKey =
  | 'PreviewBack_X'
  | 'PreviewBack_Y'
  | 'PreviewBack_W'
  | 'PreviewBack_H'
  | 'Preview_X'
  | 'Preview_Y'
  | 'Preview_W'
  | 'Preview_H'

type GoodsPreviewLayout = {
  index: number
  goods: SettingsPaymentGoodsDraft
  goodBackUrl: string
  previewUrl: string
  previewBackUrl: string
  buyBtnUrl: string
  boughtUrl: string
  noUpUrl: string
  goodsBackStyle: CSSProperties
  previewBackStyle: CSSProperties
  previewStyle: CSSProperties
  nameStyle: CSSProperties
  flowerStyle: CSSProperties
  priceStyle: CSSProperties
  introStyle: CSSProperties
  statusStyle: CSSProperties
}

type StoryLockUsageItem = {
  key: string
  order: number
  storyName: string
  position: string
}

const props = defineProps<SettingsPaymentTabProps>()

const MAX_MALL_GOODS_COUNT = 48
const MALL_GOODS_PER_PAGE = 6
const workspace = useWorkspaceStore()
const propertyOperationOptions = VARIABLE_OPERATOR_OPTIONS

const selectedGoodsIndex = ref(0)
const activePaymentFocusTarget = ref('goods-back')
const goodsDialogOpen = ref(false)
const goodsDialogSelectedIndex = ref(0)
const goodsDialogGoodsDrafts = ref<SettingsPaymentGoodsDraft[]>([])

const detailDialogOpen = ref(false)
const detailDialogDraft = ref<SettingsPaymentDetailDraft | null>(null)
const detailPreviewStageSize = ref({ width: 0, height: 0 })
const detailPreviewDragState = ref<{
  target: DetailDragTarget
  startX: number
  startY: number
  originX: number
  originY: number
} | null>(null)
const detailPreviewResizeState = ref<{
  target: DetailResizeTarget
  startX: number
  startY: number
  originW: number
  originH: number
} | null>(null)
const mallDesignWidth = computed(() => Math.max(1, Number(props.screenWidth || 1280)))
const mallDesignHeight = computed(() => Math.max(1, Number(props.screenHeight || 720)))
const mallPreviewHostStyle = computed(() => ({
  '--payment-preview-aspect': `${mallDesignWidth.value} / ${mallDesignHeight.value}`,
}))

const imageSizeMap = ref<Record<string, { width: number; height: number }>>({})

function cloneFileDraft(source: Partial<SettingsFileDraft> | null | undefined): SettingsFileDraft {
  return {
    type: Number(source?.type ?? 0),
    name: source?.name ?? '',
  }
}

function cloneViewportDraft(source: Partial<SettingsViewportDraft> | null | undefined): SettingsViewportDraft {
  return {
    X: Number(source?.X ?? 0),
    Y: Number(source?.Y ?? 0),
    Width: Number(source?.Width ?? 0),
    Height: Number(source?.Height ?? 0),
  }
}

function cloneButtonIndexDraft(source: Partial<SettingsButtonIndexDraft> | null | undefined): SettingsButtonIndexDraft {
  return {
    Index: Number(source?.Index ?? 0),
    X: Number(source?.X ?? 0),
    Y: Number(source?.Y ?? 0),
  }
}

function clonePropertyDraft(source: Partial<SettingsMallPropertyDraft> | null | undefined): SettingsMallPropertyDraft {
  return {
    PropertyId: Number(source?.PropertyId ?? -1),
    Operation: Number(source?.Operation ?? -1),
    Number: Number(source?.Number ?? -1),
  }
}

function cloneMallFontDraft(source: Partial<SettingsMallFontDraft> | null | undefined, fallbackSize: number): SettingsMallFontDraft {
  return {
    Font_Size: Math.max(1, Number(source?.Font_Size ?? fallbackSize)),
    Font_name: source?.Font_name ?? '微软雅黑',
    Font_color: source?.Font_color ?? '255,255,255',
  }
}

function cloneGoodsDraft(source: Partial<SettingsPaymentGoodsDraft> | null | undefined): SettingsPaymentGoodsDraft {
  const goodsBackWidth = Math.max(1, Number(source?.GoodsBack_W ?? 160))
  const goodsBackHeight = Math.max(1, Number(source?.GoodsBack_H ?? 92))
  const previewColumnRight = Math.max(
    Number(source?.PreviewBack_X ?? 0) + Math.max(1, Number(source?.PreviewBack_W ?? 54)),
    Number(source?.Preview_X ?? 0) + Math.max(1, Number(source?.Preview_W ?? 48)),
  )
  const defaultTextX = Math.min(Math.max(previewColumnRight + 8, 62), Math.max(12, goodsBackWidth - 84))
  const defaultTextWidth = Math.max(72, goodsBackWidth - defaultTextX - 8)
  const rawIntroView = cloneViewportDraft(source?.GoodIntroView)
  const shouldNormalizeIntroView =
    rawIntroView.Width <= 0
    || rawIntroView.Height <= 0
    || rawIntroView.Width > Math.max(120, goodsBackWidth)
    || rawIntroView.Height > Math.max(80, goodsBackHeight)
  const normalizedIntroView = shouldNormalizeIntroView
    ? {
        X: defaultTextX,
        Y: 32,
        Width: defaultTextWidth,
        Height: Math.max(24, Math.min(30, goodsBackHeight - 40)),
      }
    : rawIntroView
  const goodsType = Number(source?.GoodsType ?? GoodsTypeEnum.Var)
  return {
    GoodsId: Number(source?.GoodsId ?? 0),
    GoodsName: source?.GoodsName ?? '',
    GoodsFont: cloneMallFontDraft(source?.GoodsFont, 21),
    GoodsType: goodsType,
    GoodsIntro: source?.GoodsIntro ?? '',
    GoodsIntroFont: cloneMallFontDraft(source?.GoodsIntroFont, 18),
    GoodsPrice: Number(source?.GoodsPrice ?? 0),
    GoodsPricePakoras: Number(source?.GoodsPricePakoras ?? 0),
    GoodsPriceFont: cloneMallFontDraft(source?.GoodsPriceFont, 18),
    GoodsName_X: Number(source?.GoodsName_X ?? 0),
    GoodsName_Y: Number(source?.GoodsName_Y ?? 0),
    GoodsPrice_X: Number(source?.GoodsPrice_X ?? 0),
    GoodsPrice_Y: Number(source?.GoodsPrice_Y ?? 0),
    Flower_X: Number(source?.Flower_X ?? 0),
    Flower_Y: Number(source?.Flower_Y ?? 0),
    GoodIntroView: normalizedIntroView,
    Good_Status: Number(source?.Good_Status ?? 1),
    CanBuyRepeat: goodsType === GoodsTypeEnum.Var && Boolean(source?.CanBuyRepeat),
    GoodBack: cloneFileDraft(source?.GoodBack),
    GoodsBack_X: Number(source?.GoodsBack_X ?? 0),
    GoodsBack_Y: Number(source?.GoodsBack_Y ?? 0),
    GoodsBack_W: Number(source?.GoodsBack_W ?? 0),
    GoodsBack_H: Number(source?.GoodsBack_H ?? 0),
    Preview: cloneFileDraft(source?.Preview),
    Preview_X: Number(source?.Preview_X ?? 0),
    Preview_Y: Number(source?.Preview_Y ?? 0),
    Preview_W: Number(source?.Preview_W ?? 0),
    Preview_H: Number(source?.Preview_H ?? 0),
    PreviewBack: cloneFileDraft(source?.PreviewBack),
    PreviewBack_X: Number(source?.PreviewBack_X ?? 0),
    PreviewBack_Y: Number(source?.PreviewBack_Y ?? 0),
    PreviewBack_W: Number(source?.PreviewBack_W ?? 0),
    PreviewBack_H: Number(source?.PreviewBack_H ?? 0),
    BuyBtn: cloneButtonIndexDraft(source?.BuyBtn),
    BoughtBtn: cloneFileDraft(source?.BoughtBtn),
    NoUpPic: cloneFileDraft(source?.NoUpPic),
    PropertyList: (source?.PropertyList ?? []).map((item) => clonePropertyDraft(item)),
  }
}

function cloneDetailPageDraft(source: Partial<SettingsPaymentDetailDraft> | null | undefined): SettingsPaymentDetailDraft {
  return {
    DetailGraph: cloneFileDraft(source?.DetailGraph),
    PreviewBackGraph: cloneFileDraft(source?.PreviewBackGraph),
    PreviewBack_X: Number(source?.PreviewBack_X ?? 0),
    PreviewBack_Y: Number(source?.PreviewBack_Y ?? 0),
    PreviewBack_W: Number(source?.PreviewBack_W ?? 0),
    PreviewBack_H: Number(source?.PreviewBack_H ?? 0),
    Preview_X: Number(source?.Preview_X ?? 0),
    Preview_Y: Number(source?.Preview_Y ?? 0),
    Preview_W: Number(source?.Preview_W ?? 0),
    Preview_H: Number(source?.Preview_H ?? 0),
    GoodName_X: Number(source?.GoodName_X ?? 0),
    GoodName_Y: Number(source?.GoodName_Y ?? 0),
    FlowerTotal_X: Number(source?.FlowerTotal_X ?? 0),
    FlowerTotal_Y: Number(source?.FlowerTotal_Y ?? 0),
    Flower_X: Number(source?.Flower_X ?? 0),
    Flower_Y: Number(source?.Flower_Y ?? 0),
    GoodPrice_X: Number(source?.GoodPrice_X ?? 0),
    GoodPrice_Y: Number(source?.GoodPrice_Y ?? 0),
    BuyCount_X: Number(source?.BuyCount_X ?? 0),
    BuyCount_Y: Number(source?.BuyCount_Y ?? 0),
    GoodIntroView: cloneViewportDraft(source?.GoodIntroView),
    Ok_btn: cloneButtonIndexDraft(source?.Ok_btn),
    Cancel_btn: cloneButtonIndexDraft(source?.Cancel_btn),
    Add_btn: cloneButtonIndexDraft(source?.Add_btn),
    Minus_btn: cloneButtonIndexDraft(source?.Minus_btn),
    IsUseImageIndex: Boolean(source?.IsUseImageIndex),
    Index: Number(source?.Index ?? 1),
    IndexImage: cloneFileDraft(source?.IndexImage),
    IsShowBuyCount: Boolean(source?.IsShowBuyCount),
    IsShowGoodName: Boolean(source?.IsShowGoodName),
    IsShowGoodIntro: Boolean(source?.IsShowGoodIntro),
    IsShowGoodImage: Boolean(source?.IsShowGoodImage),
  }
}

function applyFileDraft(target: SettingsFileDraft, draft: SettingsFileDraft) {
  if (!target || !draft) return
  target.type = Number(draft.type ?? target.type ?? 0)
  target.name = String(draft.name ?? '')
}

function applyViewportDraft(target: SettingsViewportDraft, draft: SettingsViewportDraft) {
  if (!target || !draft) return
  target.X = Number(draft.X ?? 0)
  target.Y = Number(draft.Y ?? 0)
  target.Width = Number(draft.Width ?? 0)
  target.Height = Number(draft.Height ?? 0)
}

function applyButtonIndexDraft(target: SettingsButtonIndexDraft, draft: SettingsButtonIndexDraft) {
  if (!target || !draft) return
  target.Index = Number(draft.Index ?? 0)
  target.X = Number(draft.X ?? 0)
  target.Y = Number(draft.Y ?? 0)
}

function applyMallFontDraft(target: SettingsMallFontDraft, draft: SettingsMallFontDraft) {
  if (!target || !draft) return
  target.Font_Size = Math.max(1, Number(draft.Font_Size ?? 1))
  target.Font_name = String(draft.Font_name ?? '')
  target.Font_color = String(draft.Font_color ?? '255,255,255')
}

function applyGoodsDraft(target: SettingsPaymentGoodsDraft, draft: SettingsPaymentGoodsDraft) {
  if (!target || !draft) return
  target.GoodsId = Number(draft.GoodsId ?? 0)
  target.GoodsName = String(draft.GoodsName ?? '')
  applyMallFontDraft(target.GoodsFont, draft.GoodsFont)
  target.GoodsType = Number(draft.GoodsType ?? GoodsTypeEnum.Var)
  target.GoodsIntro = String(draft.GoodsIntro ?? '')
  applyMallFontDraft(target.GoodsIntroFont, draft.GoodsIntroFont)
  target.GoodsPrice = Number(draft.GoodsPrice ?? 0)
  target.GoodsPricePakoras = Number(draft.GoodsPricePakoras ?? 0)
  applyMallFontDraft(target.GoodsPriceFont, draft.GoodsPriceFont)
  target.GoodsName_X = Number(draft.GoodsName_X ?? 0)
  target.GoodsName_Y = Number(draft.GoodsName_Y ?? 0)
  target.GoodsPrice_X = Number(draft.GoodsPrice_X ?? 0)
  target.GoodsPrice_Y = Number(draft.GoodsPrice_Y ?? 0)
  target.Flower_X = Number(draft.Flower_X ?? 0)
  target.Flower_Y = Number(draft.Flower_Y ?? 0)
  applyViewportDraft(target.GoodIntroView, draft.GoodIntroView)
  target.Good_Status = Number(draft.Good_Status ?? 1)
  target.CanBuyRepeat = canGoodsRepeat(target) && Boolean(draft.CanBuyRepeat)
  applyFileDraft(target.GoodBack, draft.GoodBack)
  target.GoodsBack_X = Number(draft.GoodsBack_X ?? 0)
  target.GoodsBack_Y = Number(draft.GoodsBack_Y ?? 0)
  target.GoodsBack_W = Number(draft.GoodsBack_W ?? 0)
  target.GoodsBack_H = Number(draft.GoodsBack_H ?? 0)
  applyFileDraft(target.Preview, draft.Preview)
  target.Preview_X = Number(draft.Preview_X ?? 0)
  target.Preview_Y = Number(draft.Preview_Y ?? 0)
  target.Preview_W = Number(draft.Preview_W ?? 0)
  target.Preview_H = Number(draft.Preview_H ?? 0)
  applyFileDraft(target.PreviewBack, draft.PreviewBack)
  target.PreviewBack_X = Number(draft.PreviewBack_X ?? 0)
  target.PreviewBack_Y = Number(draft.PreviewBack_Y ?? 0)
  target.PreviewBack_W = Number(draft.PreviewBack_W ?? 0)
  target.PreviewBack_H = Number(draft.PreviewBack_H ?? 0)
  applyButtonIndexDraft(target.BuyBtn, draft.BuyBtn)
  applyFileDraft(target.BoughtBtn, draft.BoughtBtn)
  applyFileDraft(target.NoUpPic, draft.NoUpPic)
  const nextProperties = (draft.PropertyList ?? []).map((item) => clonePropertyDraft(item))
  if (Array.isArray(target.PropertyList)) {
    target.PropertyList.splice(0, target.PropertyList.length, ...nextProperties)
  } else {
    target.PropertyList = nextProperties
  }
}

function applyDetailPageDraft(target: SettingsPaymentDetailDraft, draft: SettingsPaymentDetailDraft) {
  if (!target || !draft) return
  applyFileDraft(target.DetailGraph, draft.DetailGraph)
  applyFileDraft(target.PreviewBackGraph, draft.PreviewBackGraph)
  target.PreviewBack_X = Number(draft.PreviewBack_X ?? 0)
  target.PreviewBack_Y = Number(draft.PreviewBack_Y ?? 0)
  target.PreviewBack_W = Number(draft.PreviewBack_W ?? 0)
  target.PreviewBack_H = Number(draft.PreviewBack_H ?? 0)
  target.Preview_X = Number(draft.Preview_X ?? 0)
  target.Preview_Y = Number(draft.Preview_Y ?? 0)
  target.Preview_W = Number(draft.Preview_W ?? 0)
  target.Preview_H = Number(draft.Preview_H ?? 0)
  target.GoodName_X = Number(draft.GoodName_X ?? 0)
  target.GoodName_Y = Number(draft.GoodName_Y ?? 0)
  target.FlowerTotal_X = Number(draft.FlowerTotal_X ?? 0)
  target.FlowerTotal_Y = Number(draft.FlowerTotal_Y ?? 0)
  target.Flower_X = Number(draft.Flower_X ?? 0)
  target.Flower_Y = Number(draft.Flower_Y ?? 0)
  target.GoodPrice_X = Number(draft.GoodPrice_X ?? 0)
  target.GoodPrice_Y = Number(draft.GoodPrice_Y ?? 0)
  target.BuyCount_X = Number(draft.BuyCount_X ?? 0)
  target.BuyCount_Y = Number(draft.BuyCount_Y ?? 0)
  applyViewportDraft(target.GoodIntroView, draft.GoodIntroView)
  applyButtonIndexDraft(target.Ok_btn, draft.Ok_btn)
  applyButtonIndexDraft(target.Cancel_btn, draft.Cancel_btn)
  applyButtonIndexDraft(target.Add_btn, draft.Add_btn)
  applyButtonIndexDraft(target.Minus_btn, draft.Minus_btn)
  target.IsUseImageIndex = Boolean(draft.IsUseImageIndex)
  target.Index = Number(draft.Index ?? 1)
  applyFileDraft(target.IndexImage, draft.IndexImage)
  target.IsShowBuyCount = Boolean(draft.IsShowBuyCount)
  target.IsShowGoodName = Boolean(draft.IsShowGoodName)
  target.IsShowGoodIntro = Boolean(draft.IsShowGoodIntro)
  target.IsShowGoodImage = Boolean(draft.IsShowGoodImage)
}

function toPx(value: unknown) {
  return `${Number(value ?? 0)}px`
}

function makeRectStyle(x: number, y: number, width: number, height: number): CSSProperties {
  return {
    left: `${x}px`,
    top: `${y}px`,
    width: `${Math.max(1, width)}px`,
    height: `${Math.max(1, height)}px`,
  }
}

function resolveGraphicUrl(storedPath: string) {
  return resolveResourceUrlFromFileName(
    workspace.virtualResourceRoots.graphic,
    'graphic',
    storedPath,
    workspace.resourcePreviewOverrides,
    props.graphicBaseFolderPath,
  )
}

function resolveImageOptionUrl(index: number) {
  if (index <= 0) return ''
  const item = props.imageOptionPickerItems[index]
  if (!item) return ''
  return (
    resolveResourceUrlFromFileName(
      workspace.virtualResourceRoots.graphic,
      'graphic',
      item.noneImagePath || '',
      workspace.resourcePreviewOverrides,
      props.imageOptionGraphicBaseFolderPath,
    )
    || resolveResourceUrlFromFileName(
      workspace.virtualResourceRoots.graphic,
      'graphic',
      item.moveImagePath || '',
      workspace.resourcePreviewOverrides,
      props.imageOptionGraphicBaseFolderPath,
    )
  )
}

function rememberImageSize(url: string, width: number, height: number) {
  const key = String(url || '').trim()
  if (!key || width <= 0 || height <= 0) return
  const current = imageSizeMap.value[key]
  if (current && current.width === width && current.height === height) return
  imageSizeMap.value = {
    ...imageSizeMap.value,
    [key]: { width, height },
  }
}

function getImageSize(url: string, fallbackWidth: number, fallbackHeight: number) {
  const size = imageSizeMap.value[String(url || '').trim()]
  return {
    width: Math.max(1, size?.width || fallbackWidth),
    height: Math.max(1, size?.height || fallbackHeight),
  }
}

function makeImageItem(
  key: string,
  style: CSSProperties,
  imageUrl: string,
  placeholderText: string,
  fallbackWidth = 80,
  fallbackHeight = 40,
): PreviewDrawItem | null {
  const resolvedUrl = String(imageUrl || '').trim()
  if (!resolvedUrl) return null
  return {
    key,
    kind: 'image',
    style,
    imageUrl: resolvedUrl,
    placeholderText,
    fillColor: '#334155',
    strokeColor: '#94a3b8',
    onImageLoad: ({ url, width, height }: { url: string; width: number; height: number }) => {
      rememberImageSize(url, width, height)
    },
  }
}

function compactPreviewItems<T>(items: Array<T | null | undefined>) {
  return items.filter(Boolean) as T[]
}

function outlineStyle(target: string, style: CSSProperties, color: string, active = false): CSSProperties {
  return {
    ...style,
    cursor: 'move',
    outline: active ? `2px solid ${color}` : '1px dashed transparent',
  }
}

function makeResizeHandle(
  borderColor: string,
  background: string,
  ariaLabel: string,
  onPointerdown: (event: PointerEvent) => void,
): NonNullable<PreviewHotspotItem['resizeHandle']> {
  return {
    style: {
      position: 'absolute',
      right: '0',
      bottom: '0',
      width: '14px',
      height: '14px',
      borderLeft: `1px solid ${borderColor}`,
      borderTop: `1px solid ${borderColor}`,
      background,
      cursor: 'nwse-resize',
    },
    ariaLabel,
    onPointerdown,
  }
}

function selectPaymentPreviewTarget(index: number, target: PaymentDragTarget) {
  selectedGoodsIndex.value = index
  activePaymentFocusTarget.value = target
}

function updateGoodsAt(index: number, patch: Partial<SettingsPaymentGoodsDraft>) {
  const goods = props.paymentSetting.GoodsList?.[index]
  if (!goods) return
  props.paymentSetting.GoodsList[index] = {
    ...goods,
    ...patch,
  }
}

function moveAllGoodsNumber(xKey: SharedGoodsNumberKey, yKey: SharedGoodsNumberKey, dx: number, dy: number) {
  const goods = selectedGoods.value
  if (!goods) return
  updateAllGoodsNumber(xKey, Number(goods[xKey] ?? 0) + dx)
  updateAllGoodsNumber(yKey, Number(goods[yKey] ?? 0) + dy)
}

function movePaymentPreviewTarget(target: PaymentDragTarget, index: number | undefined, dx: number, dy: number) {
  const goodsIndex = index ?? selectedGoodsIndex.value
  const goods = props.paymentSetting.GoodsList?.[goodsIndex]
  if (target === 'decorate') {
    props.paymentSetting.Decorate_X = Number(props.paymentSetting.Decorate_X ?? 0) + dx
    props.paymentSetting.Decorate_Y = Number(props.paymentSetting.Decorate_Y ?? 0) + dy
    return
  }
  if (target === 'area') {
    props.paymentSetting.GoodAreaBack_X = Number(props.paymentSetting.GoodAreaBack_X ?? 0) + dx
    props.paymentSetting.GoodAreaBack_Y = Number(props.paymentSetting.GoodAreaBack_Y ?? 0) + dy
    return
  }
  if (target === 'flower') {
    props.paymentSetting.Flower_X = Number(props.paymentSetting.Flower_X ?? 0) + dx
    props.paymentSetting.Flower_Y = Number(props.paymentSetting.Flower_Y ?? 0) + dy
    return
  }
  if (target === 'flower-own') {
    props.paymentSetting.FlowerOwn_X = Number(props.paymentSetting.FlowerOwn_X ?? 0) + dx
    props.paymentSetting.FlowerOwn_Y = Number(props.paymentSetting.FlowerOwn_Y ?? 0) + dy
    return
  }
  if (target === 'flower-price') {
    props.paymentSetting.FlowerPrice_X = Number(props.paymentSetting.FlowerPrice_X ?? 0) + dx
    props.paymentSetting.FlowerPrice_Y = Number(props.paymentSetting.FlowerPrice_Y ?? 0) + dy
    return
  }
  if (target === 'page') {
    props.paymentSetting.PageX = Number(props.paymentSetting.PageX ?? 0) + dx
    props.paymentSetting.PageY = Number(props.paymentSetting.PageY ?? 0) + dy
    return
  }
  if (target === 'close' || target === 'prev' || target === 'next' || target === 'onekey') {
    const button = target === 'close'
      ? props.paymentSetting.ClosePic
      : target === 'prev'
        ? props.paymentSetting.PrePic
        : target === 'next'
          ? props.paymentSetting.NextPic
          : props.paymentSetting.OneKeyGetBtn
    button.X = Number(button.X ?? 0) + dx
    button.Y = Number(button.Y ?? 0) + dy
    return
  }
  if (!goods) return
  if (target === 'goods-preview-back') {
    moveAllGoodsNumber('PreviewBack_X', 'PreviewBack_Y', dx, dy)
    return
  }
  if (target === 'goods-icon') {
    moveAllGoodsNumber('Preview_X', 'Preview_Y', dx, dy)
    return
  }
  if (target === 'goods-status') {
    updateAllGoodsBuyButtonPosition('X', Number(goods.BuyBtn.X ?? 0) + dx)
    updateAllGoodsBuyButtonPosition('Y', Number(goods.BuyBtn.Y ?? 0) + dy)
    return
  }
  if (target === 'goods-back') {
    updateGoodsAt(goodsIndex, {
      GoodsBack_X: Number(goods.GoodsBack_X ?? 0) + dx,
      GoodsBack_Y: Number(goods.GoodsBack_Y ?? 0) + dy,
    })
    return
  }
  if (target === 'goods-name') {
    updateGoodsAt(goodsIndex, {
      GoodsName_X: Number(goods.GoodsName_X ?? 0) + dx,
      GoodsName_Y: Number(goods.GoodsName_Y ?? 0) + dy,
    })
    return
  }
  if (target === 'goods-intro') {
    updateGoodsAt(goodsIndex, {
      GoodIntroView: {
        ...goods.GoodIntroView,
        X: Number(goods.GoodIntroView.X ?? 0) + dx,
        Y: Number(goods.GoodIntroView.Y ?? 0) + dy,
      },
    })
    return
  }
  if (target === 'goods-flower') {
    updateGoodsAt(goodsIndex, {
      Flower_X: Number(goods.Flower_X ?? 0) + dx,
      Flower_Y: Number(goods.Flower_Y ?? 0) + dy,
    })
    return
  }
  if (target === 'goods-price') {
    updateGoodsAt(goodsIndex, {
      GoodsPrice_X: Number(goods.GoodsPrice_X ?? 0) + dx,
      GoodsPrice_Y: Number(goods.GoodsPrice_Y ?? 0) + dy,
    })
  }
}

function resizePaymentPreviewTarget(target: PaymentResizeTarget, index: number | undefined, dw: number, dh: number) {
  const goodsIndex = index ?? selectedGoodsIndex.value
  const goods = props.paymentSetting.GoodsList?.[goodsIndex]
  if (target === 'area') {
    props.paymentSetting.GoodAreaBack_W = Math.max(1, Number(props.paymentSetting.GoodAreaBack_W ?? 1) + dw)
    props.paymentSetting.GoodAreaBack_H = Math.max(1, Number(props.paymentSetting.GoodAreaBack_H ?? 1) + dh)
    return
  }
  if (!goods) return
  if (target === 'goods-preview-back') {
    updateAllGoodsNumber('PreviewBack_W', Math.max(1, Number(goods.PreviewBack_W ?? 1) + dw))
    updateAllGoodsNumber('PreviewBack_H', Math.max(1, Number(goods.PreviewBack_H ?? 1) + dh))
    return
  }
  if (target === 'goods-icon') {
    updateAllGoodsNumber('Preview_W', Math.max(1, Number(goods.Preview_W ?? 1) + dw))
    updateAllGoodsNumber('Preview_H', Math.max(1, Number(goods.Preview_H ?? 1) + dh))
    return
  }
  if (target === 'goods-back') {
    updateGoodsAt(goodsIndex, {
      GoodsBack_W: Math.max(1, Number(goods.GoodsBack_W ?? 1) + dw),
      GoodsBack_H: Math.max(1, Number(goods.GoodsBack_H ?? 1) + dh),
    })
    return
  }
  updateGoodsAt(goodsIndex, {
    GoodIntroView: {
      ...goods.GoodIntroView,
      Width: Math.max(1, Number(goods.GoodIntroView.Width ?? 1) + dw),
      Height: Math.max(1, Number(goods.GoodIntroView.Height ?? 1) + dh),
    },
  })
}

function detailOutlineStyle(target: string, style: CSSProperties, color: string): CSSProperties {
  return {
    ...style,
    cursor: 'move',
    outline: detailPreviewDragState.value?.target === target ? `1px dashed ${color}` : '1px dashed transparent',
  }
}

const goodsTypeOptions = [
  { value: GoodsTypeEnum.Map, label: '剧情' },
  { value: GoodsTypeEnum.Var, label: '数值' },
  { value: GoodsTypeEnum.MaxPack, label: '大礼包' },
  { value: GoodsTypeEnum.Dress, label: '装饰' },
]

function canGoodsRepeat(goods: Pick<SettingsPaymentGoodsDraft, 'GoodsType'> | null | undefined): boolean {
  return Number(goods?.GoodsType ?? GoodsTypeEnum.Var) === GoodsTypeEnum.Var
}

function getMallFontSize(font: SettingsMallFontDraft | null | undefined, fallback: number): number {
  return Math.max(1, Number(font?.Font_Size ?? fallback))
}

function getMallFontColor(font: SettingsMallFontDraft | null | undefined, fallback: string): string {
  const raw = String(font?.Font_color || fallback).trim()
  if (/^#[0-9a-f]{6}$/i.test(raw)) return raw
  const match = raw.match(/^(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})$/)
  if (!match) return fallback.startsWith('#') ? fallback : `rgb(${fallback})`
  const channels = match.slice(1).map((item) => Math.max(0, Math.min(255, Number(item) || 0)))
  return `rgb(${channels.join(', ')})`
}

function getMallFontFamily(font: SettingsMallFontDraft | null | undefined): string {
  const family = String(font?.Font_name || '').trim()
  return family ? `"${family}", sans-serif` : 'sans-serif'
}

watch(
  () => props.paymentSetting.GoodsList?.length ?? 0,
  (length, oldLength) => {
    if (length <= 0) {
      selectedGoodsIndex.value = 0
      goodsDialogSelectedIndex.value = 0
      goodsDialogOpen.value = false
      return
    }
    if (oldLength !== undefined && length > oldLength) {
      selectedGoodsIndex.value = length - 1
      activePaymentFocusTarget.value = 'goods-back'
    }
    selectedGoodsIndex.value = Math.min(selectedGoodsIndex.value, length - 1)
    goodsDialogSelectedIndex.value = Math.min(goodsDialogSelectedIndex.value, length - 1)
  },
  { immediate: true },
)

const goodsSummaryItems = computed(() =>
  (props.paymentSetting.GoodsList ?? []).map((item, index: number) => ({
    index,
    name: item.GoodsName?.trim() || `商品${index + 1}`,
    price: Number(item.GoodsPrice ?? 0),
    typeText: goodsTypeOptions.find((option) => option.value === Number(item.GoodsType ?? GoodsTypeEnum.Var))?.label ?? '未知',
    statusText: Number(item.Good_Status ?? 1) === 1 ? '已上架' : '未上架',
  })),
)

const selectedGoods = computed(() => props.paymentSetting.GoodsList?.[selectedGoodsIndex.value] ?? null)
const mallPriceTextFont = computed(() => props.paymentSetting.GoodsList?.[0]?.GoodsPriceFont ?? null)

const previewPageIndex = computed(() =>
  Math.max(0, Math.floor(selectedGoodsIndex.value / MALL_GOODS_PER_PAGE)),
)

const previewPageCount = computed(() =>
  Math.max(1, Math.ceil((props.paymentSetting.GoodsList?.length ?? 0) / MALL_GOODS_PER_PAGE)),
)

const selectedGoodsLayout = computed<GoodsPreviewLayout | null>(() =>
  goodsPreviewLayouts.value.find((item) => item.index === selectedGoodsIndex.value) ?? null,
)

const visibleGoodsPreviewLayouts = computed<GoodsPreviewLayout[]>(() => {
  const start = previewPageIndex.value * MALL_GOODS_PER_PAGE
  const end = start + MALL_GOODS_PER_PAGE
  return goodsPreviewLayouts.value.filter((item) => item.index >= start && item.index < end)
})

const mallBackgroundUrl = computed(() => resolveGraphicUrl(props.paymentSetting.MallBack?.name ?? ''))
const mallDecorateUrl = computed(() => resolveGraphicUrl(props.paymentSetting.DecorateBack?.name ?? ''))
const mallFlowerUrl = computed(() => resolveGraphicUrl(props.paymentSetting.FlowerPic?.name ?? ''))
const mallGoodAreaBackUrl = computed(() => resolveGraphicUrl(props.paymentSetting.GoodAreaBack?.name ?? ''))
const mallCloseButtonUrl = computed(() => resolveImageOptionUrl(Number(props.paymentSetting.ClosePic?.Index ?? 0)))
const mallPrevButtonUrl = computed(() => resolveImageOptionUrl(Number(props.paymentSetting.PrePic?.Index ?? 0)))
const mallNextButtonUrl = computed(() => resolveImageOptionUrl(Number(props.paymentSetting.NextPic?.Index ?? 0)))
const mallOneKeyButtonUrl = computed(() => resolveImageOptionUrl(Number(props.paymentSetting.OneKeyGetBtn?.Index ?? 0)))

const mallCloseButtonStyle = computed(() => {
  const size = getImageSize(mallCloseButtonUrl.value, 72, 32)
  return makeRectStyle(Number(props.paymentSetting.ClosePic?.X ?? 0), Number(props.paymentSetting.ClosePic?.Y ?? 0), size.width, size.height)
})

const mallPrevButtonStyle = computed(() => {
  const size = getImageSize(mallPrevButtonUrl.value, 72, 32)
  return makeRectStyle(Number(props.paymentSetting.PrePic?.X ?? 0), Number(props.paymentSetting.PrePic?.Y ?? 0), size.width, size.height)
})

const mallNextButtonStyle = computed(() => {
  const size = getImageSize(mallNextButtonUrl.value, 72, 32)
  return makeRectStyle(Number(props.paymentSetting.NextPic?.X ?? 0), Number(props.paymentSetting.NextPic?.Y ?? 0), size.width, size.height)
})

const mallOneKeyButtonStyle = computed(() => {
  const size = getImageSize(mallOneKeyButtonUrl.value, 120, 36)
  return makeRectStyle(Number(props.paymentSetting.OneKeyGetBtn?.X ?? 0), Number(props.paymentSetting.OneKeyGetBtn?.Y ?? 0), size.width, size.height)
})

const mallDecorateStyle = computed(() => {
  const size = getImageSize(mallDecorateUrl.value, 180, 96)
  return makeRectStyle(Number(props.paymentSetting.Decorate_X ?? 0), Number(props.paymentSetting.Decorate_Y ?? 0), size.width, size.height)
})

const mallFlowerPicStyle = computed(() => {
  const size = getImageSize(mallFlowerUrl.value, 18, 18)
  return makeRectStyle(Number(props.paymentSetting.Flower_X ?? 0), Number(props.paymentSetting.Flower_Y ?? 0), size.width, size.height)
})

const mallGoodAreaStyle = computed(() =>
  makeRectStyle(
    Number(props.paymentSetting.GoodAreaBack_X ?? 0),
    Number(props.paymentSetting.GoodAreaBack_Y ?? 0),
    Math.max(1, Number(props.paymentSetting.GoodAreaBack_W ?? getImageSize(mallGoodAreaBackUrl.value, 320, 320).width)),
    Math.max(1, Number(props.paymentSetting.GoodAreaBack_H ?? getImageSize(mallGoodAreaBackUrl.value, 320, 320).height)),
  ),
)

const goodsPreviewLayouts = computed<GoodsPreviewLayout[]>(() => {
  const goods = props.paymentSetting.GoodsList ?? []
  if (!goods.length) return []
  const flowerSize = getImageSize(mallFlowerUrl.value, 16, 16)

  return goods.map((item, index: number) => {
    const goodsBackX = Number(item.GoodsBack_X || 0)
    const goodsBackY = Number(item.GoodsBack_Y || 0)
    const goodsBackWidth = Math.max(1, Number(item.GoodsBack_W || 160))
    const goodsBackHeight = Math.max(1, Number(item.GoodsBack_H || 92))
    const previewBackWidth = Math.max(1, Number(item.PreviewBack_W || 72))
    const previewBackHeight = Math.max(1, Number(item.PreviewBack_H || 72))
    const previewWidth = Math.max(1, Number(item.Preview_W || 64))
    const previewHeight = Math.max(1, Number(item.Preview_H || 64))
    const introWidth = Math.max(48, Number(item.GoodIntroView?.Width || goodsBackWidth - 20))
    const introHeight = Math.max(18, Number(item.GoodIntroView?.Height || 36))
    const buyBtnUrl = resolveImageOptionUrl(Number(item.BuyBtn?.Index ?? 0))
    const buyBtnSize = getImageSize(buyBtnUrl, 72, 32)
    return {
      index,
      goods: item,
      goodBackUrl: resolveGraphicUrl(item.GoodBack?.name ?? ''),
      previewUrl: resolveGraphicUrl(item.Preview?.name ?? ''),
      previewBackUrl: resolveGraphicUrl(item.PreviewBack?.name ?? ''),
      buyBtnUrl,
      boughtUrl: resolveGraphicUrl(item.BoughtBtn?.name ?? ''),
      noUpUrl: resolveGraphicUrl(item.NoUpPic?.name ?? ''),
      goodsBackStyle: makeRectStyle(goodsBackX, goodsBackY, goodsBackWidth, goodsBackHeight),
      previewBackStyle: makeRectStyle(goodsBackX + Number(item.PreviewBack_X || 0), goodsBackY + Number(item.PreviewBack_Y || 0), previewBackWidth, previewBackHeight),
      previewStyle: makeRectStyle(goodsBackX + Number(item.Preview_X || 0), goodsBackY + Number(item.Preview_Y || 0), previewWidth, previewHeight),
      nameStyle: makeRectStyle(goodsBackX + Number(item.GoodsName_X || 0), goodsBackY + Number(item.GoodsName_Y || 0), Math.max(72, goodsBackWidth - Number(item.GoodsName_X || 0) - 8), 18),
      flowerStyle: makeRectStyle(goodsBackX + Number(item.Flower_X || 0), goodsBackY + Number(item.Flower_Y || 0), flowerSize.width, flowerSize.height),
      priceStyle: makeRectStyle(goodsBackX + Number(item.GoodsPrice_X || 0), goodsBackY + Number(item.GoodsPrice_Y || 0), Math.max(72, goodsBackWidth - Number(item.GoodsPrice_X || 0) - 8), 18),
      introStyle: makeRectStyle(goodsBackX + Number(item.GoodIntroView?.X || 0), goodsBackY + Number(item.GoodIntroView?.Y || 0), introWidth, introHeight),
      statusStyle: makeRectStyle(goodsBackX + Number(item.BuyBtn?.X || 0), goodsBackY + Number(item.BuyBtn?.Y || 0), buyBtnSize.width, buyBtnSize.height),
    }
  })
})

const previewDrawItems = computed<PreviewDrawItem[]>(() => {
  const items: Array<PreviewDrawItem | null> = [
    makeImageItem('good-area-back', mallGoodAreaStyle.value, mallGoodAreaBackUrl.value, '商品底板'),
    makeImageItem('decorate', mallDecorateStyle.value, mallDecorateUrl.value, '商城装饰'),
    makeImageItem('flower-pic', mallFlowerPicStyle.value, mallFlowerUrl.value, '鲜花'),
    {
      key: 'flower-own',
      kind: 'text' as const,
      x: Number(props.paymentSetting.FlowerOwn_X ?? 0),
      y: Number(props.paymentSetting.FlowerOwn_Y ?? 0),
      width: 96,
      height: 18,
      text: '剩余鲜花',
      fontSize: getMallFontSize(mallPriceTextFont.value, 18),
      fontFamily: getMallFontFamily(mallPriceTextFont.value),
      textColor: getMallFontColor(mallPriceTextFont.value, '#fde68a'),
    },
    {
      key: 'flower-price',
      kind: 'text' as const,
      x: Number(props.paymentSetting.FlowerPrice_X ?? 0),
      y: Number(props.paymentSetting.FlowerPrice_Y ?? 0),
      width: 80,
      height: 18,
      text: '9999朵',
      fontSize: getMallFontSize(mallPriceTextFont.value, 18),
      fontFamily: getMallFontFamily(mallPriceTextFont.value),
      textColor: getMallFontColor(mallPriceTextFont.value, '#fde68a'),
    },
  ]

  for (const layout of visibleGoodsPreviewLayouts.value) {
    items.push(makeImageItem(`goods-back-${layout.index}`, layout.goodsBackStyle, layout.goodBackUrl, '商品底板'))

    if (props.paymentSetting.IsShowGoodImage) {
      items.push(
        makeImageItem(`goods-preview-back-${layout.index}`, layout.previewBackStyle, layout.previewBackUrl, '图标底图'),
        makeImageItem(`goods-preview-${layout.index}`, layout.previewStyle, layout.previewUrl, '商品图标'),
      )
    }

    if (props.paymentSetting.IsShowGoodName) {
      items.push({
        key: `goods-name-${layout.index}`,
        kind: 'text' as const,
        style: layout.nameStyle,
        text: layout.goods.GoodsName || `商品${layout.index + 1}`,
        fontSize: getMallFontSize(layout.goods.GoodsFont, 21),
        fontFamily: getMallFontFamily(layout.goods.GoodsFont),
        textColor: getMallFontColor(layout.goods.GoodsFont, '#ffffff'),
      })
    }

    items.push(makeImageItem(`goods-flower-${layout.index}`, layout.flowerStyle, mallFlowerUrl.value, '花', 16, 16))

    items.push({
      key: `goods-price-${layout.index}`,
      kind: 'text' as const,
      style: layout.priceStyle,
      text: `${Number(layout.goods.GoodsPrice ?? 0)}朵`,
      fontSize: getMallFontSize(layout.goods.GoodsPriceFont, 18),
      fontFamily: getMallFontFamily(layout.goods.GoodsPriceFont),
      textColor: getMallFontColor(layout.goods.GoodsPriceFont, '#fde68a'),
    })

    if (props.paymentSetting.IsShowGoodIntro) {
      items.push({
        key: `goods-intro-${layout.index}`,
        kind: 'text' as const,
        style: layout.introStyle,
        text: layout.goods.GoodsIntro || '商品简介',
        fontSize: getMallFontSize(layout.goods.GoodsIntroFont, 18),
        fontFamily: getMallFontFamily(layout.goods.GoodsIntroFont),
        textColor: getMallFontColor(layout.goods.GoodsIntroFont, '#d1d5db'),
      })
    }

    const statusUrl = Number(layout.goods.Good_Status ?? 1) === 1 ? layout.buyBtnUrl : layout.noUpUrl
    if (statusUrl) {
      items.push(makeImageItem(`goods-status-${layout.index}`, layout.statusStyle, statusUrl, '商品状态'))
    }
  }

  if (mallCloseButtonUrl.value) {
    items.push(makeImageItem('close', mallCloseButtonStyle.value, mallCloseButtonUrl.value, '关闭'))
  }
  if (mallPrevButtonUrl.value) {
    items.push(makeImageItem('prev', mallPrevButtonStyle.value, mallPrevButtonUrl.value, '上一页'))
  }
  if (mallNextButtonUrl.value) {
    items.push(makeImageItem('next', mallNextButtonStyle.value, mallNextButtonUrl.value, '下一页'))
  }
  items.push({
      key: 'page',
      kind: 'text' as const,
      x: Number(props.paymentSetting.PageX ?? 0),
      y: Number(props.paymentSetting.PageY ?? 0),
      width: 80,
      height: 18,
      text: `${previewPageIndex.value + 1}/${previewPageCount.value}`,
      fontSize: getMallFontSize(mallPriceTextFont.value, 18),
      fontFamily: getMallFontFamily(mallPriceTextFont.value),
      textColor: getMallFontColor(mallPriceTextFont.value, '#fde68a'),
    })

  if (props.paymentSetting.IsShowOneKeyGetBtn && mallOneKeyButtonUrl.value) {
    items.push(makeImageItem('onekey', mallOneKeyButtonStyle.value, mallOneKeyButtonUrl.value, '一键领取', 120, 36))
  }

  return compactPreviewItems(items)
})

const previewHotspotItems = computed<PreviewHotspotItem[]>(() => {
  const items: PreviewHotspotItem[] = [
    {
      key: 'decorate',
      style: outlineStyle('decorate', mallDecorateStyle.value, '#f97316'),
      ariaLabel: '商城装饰拖拽热区',
      onPointerdown: (event: PointerEvent) => props.startPaymentPreviewDrag('decorate', event),
      onKeyboardMove: ({ dx, dy }) => movePaymentPreviewTarget('decorate', undefined, dx, dy),
    },
    {
      key: 'area',
      style: outlineStyle('area', mallGoodAreaStyle.value, '#f59e0b'),
      ariaLabel: '商品区域拖拽热区',
      onPointerdown: (event: PointerEvent) => props.startPaymentPreviewDrag('area', event),
      onKeyboardMove: ({ dx, dy }) => movePaymentPreviewTarget('area', undefined, dx, dy),
      onKeyboardResize: ({ dw, dh }) => resizePaymentPreviewTarget('area', undefined, dw, dh),
      resizeHandle: makeResizeHandle('#fbbf24', '#a16207', '调整商品区域大小', (event) => props.startPaymentPreviewResize('area', event)),
    },
    {
      key: 'flower-pic',
      style: outlineStyle('flower', mallFlowerPicStyle.value, '#38bdf8'),
      ariaLabel: '鲜花图标拖拽热区',
      onPointerdown: (event: PointerEvent) => props.startPaymentPreviewDrag('flower', event),
      onKeyboardMove: ({ dx, dy }) => movePaymentPreviewTarget('flower', undefined, dx, dy),
    },
    {
      key: 'flower-own',
      style: outlineStyle('flower-own', makeRectStyle(Number(props.paymentSetting.FlowerOwn_X ?? 0), Number(props.paymentSetting.FlowerOwn_Y ?? 0), 96, 18), '#22c55e'),
      ariaLabel: '剩余鲜花文字拖拽热区',
      onPointerdown: (event: PointerEvent) => props.startPaymentPreviewDrag('flower-own', event),
      onKeyboardMove: ({ dx, dy }) => movePaymentPreviewTarget('flower-own', undefined, dx, dy),
    },
    {
      key: 'flower-price',
      style: outlineStyle('flower-price', makeRectStyle(Number(props.paymentSetting.FlowerPrice_X ?? 0), Number(props.paymentSetting.FlowerPrice_Y ?? 0), 80, 18), '#eab308'),
      ariaLabel: '鲜花数值拖拽热区',
      onPointerdown: (event: PointerEvent) => props.startPaymentPreviewDrag('flower-price', event),
      onKeyboardMove: ({ dx, dy }) => movePaymentPreviewTarget('flower-price', undefined, dx, dy),
    },
    {
      key: 'page',
      style: outlineStyle('page', makeRectStyle(Number(props.paymentSetting.PageX ?? 0), Number(props.paymentSetting.PageY ?? 0), 80, 18), '#a855f7'),
      ariaLabel: '页码拖拽热区',
      onPointerdown: (event: PointerEvent) => props.startPaymentPreviewDrag('page', event),
      onKeyboardMove: ({ dx, dy }) => movePaymentPreviewTarget('page', undefined, dx, dy),
    },
  ]

  if (mallCloseButtonUrl.value) {
    items.push({
      key: 'close',
      style: outlineStyle('close', mallCloseButtonStyle.value, '#ef4444'),
      ariaLabel: '关闭按钮拖拽热区',
      onPointerdown: (event: PointerEvent) => props.startPaymentPreviewDrag('close', event),
      onKeyboardMove: ({ dx, dy }) => movePaymentPreviewTarget('close', undefined, dx, dy),
    })
  }

  if (mallPrevButtonUrl.value) {
    items.push({
      key: 'prev',
      style: outlineStyle('prev', mallPrevButtonStyle.value, '#22c55e'),
      ariaLabel: '上一页按钮拖拽热区',
      onPointerdown: (event: PointerEvent) => props.startPaymentPreviewDrag('prev', event),
      onKeyboardMove: ({ dx, dy }) => movePaymentPreviewTarget('prev', undefined, dx, dy),
    })
  }

  if (mallNextButtonUrl.value) {
    items.push({
      key: 'next',
      style: outlineStyle('next', mallNextButtonStyle.value, '#22c55e'),
      ariaLabel: '下一页按钮拖拽热区',
      onPointerdown: (event: PointerEvent) => props.startPaymentPreviewDrag('next', event),
      onKeyboardMove: ({ dx, dy }) => movePaymentPreviewTarget('next', undefined, dx, dy),
    })
  }

  if (props.paymentSetting.IsShowOneKeyGetBtn && mallOneKeyButtonUrl.value) {
    items.push({
      key: 'onekey',
      style: outlineStyle('onekey', mallOneKeyButtonStyle.value, '#f97316'),
      ariaLabel: '一键领取按钮拖拽热区',
      onPointerdown: (event: PointerEvent) => props.startPaymentPreviewDrag('onekey', event),
      onKeyboardMove: ({ dx, dy }) => movePaymentPreviewTarget('onekey', undefined, dx, dy),
    })
  }

  for (const layout of visibleGoodsPreviewLayouts.value) {
    items.push({
      key: `goods-back-hotspot-${layout.index}`,
      style: outlineStyle('goods-back', layout.goodsBackStyle, '#60a5fa', selectedGoodsIndex.value === layout.index && activePaymentFocusTarget.value === 'goods-back'),
      ariaLabel: '商品底板拖拽热区',
      onPointerdown: (event: PointerEvent) => {
        selectPaymentPreviewTarget(layout.index, 'goods-back')
        props.startPaymentPreviewDrag('goods-back', event, layout.index)
      },
      onKeyboardMove: ({ dx, dy }) => movePaymentPreviewTarget('goods-back', layout.index, dx, dy),
      onKeyboardResize: ({ dw, dh }) => resizePaymentPreviewTarget('goods-back', layout.index, dw, dh),
      resizeHandle: selectedGoodsIndex.value === layout.index && activePaymentFocusTarget.value === 'goods-back'
        ? makeResizeHandle('#60a5fa', '#1d4ed8', '调整商品底板大小', (event) => props.startPaymentPreviewResize('goods-back', event, layout.index))
        : undefined,
    })
    if (props.paymentSetting.IsShowGoodImage) {
      items.push(
        {
          key: `goods-preview-back-hotspot-${layout.index}`,
          style: outlineStyle('goods-preview-back', layout.previewBackStyle, '#f59e0b', activePaymentFocusTarget.value === 'goods-preview-back'),
          ariaLabel: '图标底图拖拽热区',
          onPointerdown: (event: PointerEvent) => {
            selectPaymentPreviewTarget(layout.index, 'goods-preview-back')
            props.startPaymentPreviewDrag('goods-preview-back', event, layout.index)
          },
          onKeyboardMove: ({ dx, dy }) => movePaymentPreviewTarget('goods-preview-back', layout.index, dx, dy),
          onKeyboardResize: ({ dw, dh }) => resizePaymentPreviewTarget('goods-preview-back', layout.index, dw, dh),
          resizeHandle: activePaymentFocusTarget.value === 'goods-preview-back'
            ? makeResizeHandle('#fbbf24', '#a16207', '调整图标底图大小', (event) => {
                selectPaymentPreviewTarget(layout.index, 'goods-preview-back')
                props.startPaymentPreviewResize('goods-preview-back', event, layout.index)
              })
            : undefined,
        },
        {
          key: `goods-preview-hotspot-${layout.index}`,
          style: outlineStyle('goods-icon', layout.previewStyle, '#38bdf8', activePaymentFocusTarget.value === 'goods-icon'),
          ariaLabel: '商品图标拖拽热区',
          onPointerdown: (event: PointerEvent) => {
            selectPaymentPreviewTarget(layout.index, 'goods-icon')
            props.startPaymentPreviewDrag('goods-icon', event, layout.index)
          },
          onKeyboardMove: ({ dx, dy }) => movePaymentPreviewTarget('goods-icon', layout.index, dx, dy),
          onKeyboardResize: ({ dw, dh }) => resizePaymentPreviewTarget('goods-icon', layout.index, dw, dh),
          resizeHandle: activePaymentFocusTarget.value === 'goods-icon'
            ? makeResizeHandle('#38bdf8', '#0369a1', '调整商品图标大小', (event) => {
                selectPaymentPreviewTarget(layout.index, 'goods-icon')
                props.startPaymentPreviewResize('goods-icon', event, layout.index)
              })
            : undefined,
        },
      )
    }

    if (Number(layout.goods.Good_Status ?? 1) !== 1 || layout.buyBtnUrl || layout.noUpUrl) {
      items.push({
        key: `goods-status-hotspot-${layout.index}`,
        style: outlineStyle('goods-status', layout.statusStyle, '#ef4444', activePaymentFocusTarget.value === 'goods-status'),
        ariaLabel: '商品状态拖拽热区',
        onPointerdown: (event: PointerEvent) => {
          selectPaymentPreviewTarget(layout.index, 'goods-status')
          props.startPaymentPreviewDrag('goods-status', event, layout.index)
        },
        onKeyboardMove: ({ dx, dy }) => movePaymentPreviewTarget('goods-status', layout.index, dx, dy),
      })
    }
  }

  const selected = selectedGoodsLayout.value
  if (selected) {
    items.push(
      {
        key: 'selected-name',
        style: outlineStyle('goods-name', selected.nameStyle, '#22c55e', activePaymentFocusTarget.value === 'goods-name'),
        ariaLabel: '商品名称拖拽热区',
        onPointerdown: (event: PointerEvent) => {
          selectPaymentPreviewTarget(selected.index, 'goods-name')
          props.startPaymentPreviewDrag('goods-name', event, selected.index)
        },
        onKeyboardMove: ({ dx, dy }) => movePaymentPreviewTarget('goods-name', selected.index, dx, dy),
      },
      {
        key: 'selected-flower',
        style: outlineStyle('goods-flower', selected.flowerStyle, '#f97316', activePaymentFocusTarget.value === 'goods-flower'),
        ariaLabel: '商品鲜花拖拽热区',
        onPointerdown: (event: PointerEvent) => {
          selectPaymentPreviewTarget(selected.index, 'goods-flower')
          props.startPaymentPreviewDrag('goods-flower', event, selected.index)
        },
        onKeyboardMove: ({ dx, dy }) => movePaymentPreviewTarget('goods-flower', selected.index, dx, dy),
      },
      {
        key: 'selected-price',
        style: outlineStyle('goods-price', selected.priceStyle, '#eab308', activePaymentFocusTarget.value === 'goods-price'),
        ariaLabel: '商品价格拖拽热区',
        onPointerdown: (event: PointerEvent) => {
          selectPaymentPreviewTarget(selected.index, 'goods-price')
          props.startPaymentPreviewDrag('goods-price', event, selected.index)
        },
        onKeyboardMove: ({ dx, dy }) => movePaymentPreviewTarget('goods-price', selected.index, dx, dy),
      },
      {
        key: 'selected-intro',
        style: outlineStyle('goods-intro', selected.introStyle, '#a855f7', activePaymentFocusTarget.value === 'goods-intro'),
        ariaLabel: '商品简介拖拽热区',
        onPointerdown: (event: PointerEvent) => {
          selectPaymentPreviewTarget(selected.index, 'goods-intro')
          props.startPaymentPreviewDrag('goods-intro', event, selected.index)
        },
        onKeyboardMove: ({ dx, dy }) => movePaymentPreviewTarget('goods-intro', selected.index, dx, dy),
        onKeyboardResize: ({ dw, dh }) => resizePaymentPreviewTarget('goods-intro', selected.index, dw, dh),
        resizeHandle: activePaymentFocusTarget.value === 'goods-intro'
          ? makeResizeHandle('#a855f7', '#6d28d9', '调整商品简介大小', (event) => {
              selectPaymentPreviewTarget(selected.index, 'goods-intro')
              props.startPaymentPreviewResize('goods-intro', event, selected.index)
            })
          : undefined,
      },
    )
  }

  return items
})

function openGoodsDialog() {
  goodsDialogGoodsDrafts.value = (props.paymentSetting.GoodsList ?? []).map((item) => cloneGoodsDraft(item))
  goodsDialogSelectedIndex.value = selectedGoodsIndex.value
  goodsDialogOpen.value = true
}

function closeGoodsDialog() {
  goodsDialogOpen.value = false
  goodsDialogGoodsDrafts.value = []
}

function confirmGoodsDialog() {
  goodsDialogGoodsDrafts.value.forEach((draft, index) => {
    const target = props.paymentSetting.GoodsList?.[index]
    if (!canGoodsRepeat(draft)) {
      draft.CanBuyRepeat = false
    }
    if (target) applyGoodsDraft(target, draft)
  })
  closeGoodsDialog()
}

function updateSelectedGoodsBack(value: string) {
  const nextValue = String(value)
  props.paymentSetting.GoodsList?.forEach((goods, index) => {
    goods.GoodBack.name = nextValue
    props.paymentSetting.GoodsList[index] = {
      ...goods,
      GoodBack: {
        ...goods.GoodBack,
        name: nextValue,
      },
    }
  })
}

function updateAllGoodsPreviewBack(value: string) {
  const nextValue = String(value)
  props.paymentSetting.GoodsList?.forEach((goods, index) => {
    goods.PreviewBack.name = nextValue
    props.paymentSetting.GoodsList[index] = {
      ...goods,
      PreviewBack: {
        ...goods.PreviewBack,
        name: nextValue,
      },
    }
  })
}

function updateAllGoodsBuyButtonIndex(value: number) {
  const nextValue = Number(value)
  props.paymentSetting.GoodsList?.forEach((goods, index) => {
    props.paymentSetting.GoodsList[index] = {
      ...goods,
      BuyBtn: {
        ...goods.BuyBtn,
        Index: nextValue,
      },
    }
  })
}

function updateAllGoodsBoughtPic(value: string) {
  const nextValue = String(value)
  props.paymentSetting.GoodsList?.forEach((goods, index) => {
    props.paymentSetting.GoodsList[index] = {
      ...goods,
      BoughtBtn: {
        ...goods.BoughtBtn,
        name: nextValue,
      },
    }
  })
}

function updateAllGoodsNoUpPic(value: string) {
  const nextValue = String(value)
  props.paymentSetting.GoodsList?.forEach((goods, index) => {
    props.paymentSetting.GoodsList[index] = {
      ...goods,
      NoUpPic: {
        ...goods.NoUpPic,
        name: nextValue,
      },
    }
  })
}

function updateAllGoodsNumber(key: SharedGoodsNumberKey, value: number) {
  props.paymentSetting.GoodsList?.forEach((goods, index) => {
    props.paymentSetting.GoodsList[index] = {
      ...goods,
      [key]: value,
    }
  })
}

function updateAllGoodsBuyButtonPosition(axis: 'X' | 'Y', value: number) {
  props.paymentSetting.GoodsList?.forEach((goods, index) => {
    props.paymentSetting.GoodsList[index] = {
      ...goods,
      BuyBtn: {
        ...goods.BuyBtn,
        [axis]: value,
      },
    }
  })
}

const sharedGoodsPreviewBackX = computed({
  get: () => Number(selectedGoods.value?.PreviewBack_X ?? 0),
  set: (value: number) => updateAllGoodsNumber('PreviewBack_X', value),
})
const sharedGoodsPreviewBackY = computed({
  get: () => Number(selectedGoods.value?.PreviewBack_Y ?? 0),
  set: (value: number) => updateAllGoodsNumber('PreviewBack_Y', value),
})
const sharedGoodsPreviewBackW = computed({
  get: () => Math.max(1, Number(selectedGoods.value?.PreviewBack_W ?? 1)),
  set: (value: number) => updateAllGoodsNumber('PreviewBack_W', Math.max(1, value)),
})
const sharedGoodsPreviewBackH = computed({
  get: () => Math.max(1, Number(selectedGoods.value?.PreviewBack_H ?? 1)),
  set: (value: number) => updateAllGoodsNumber('PreviewBack_H', Math.max(1, value)),
})
const sharedGoodsPreviewX = computed({
  get: () => Number(selectedGoods.value?.Preview_X ?? 0),
  set: (value: number) => updateAllGoodsNumber('Preview_X', value),
})
const sharedGoodsPreviewY = computed({
  get: () => Number(selectedGoods.value?.Preview_Y ?? 0),
  set: (value: number) => updateAllGoodsNumber('Preview_Y', value),
})
const sharedGoodsPreviewW = computed({
  get: () => Math.max(1, Number(selectedGoods.value?.Preview_W ?? 1)),
  set: (value: number) => updateAllGoodsNumber('Preview_W', Math.max(1, value)),
})
const sharedGoodsPreviewH = computed({
  get: () => Math.max(1, Number(selectedGoods.value?.Preview_H ?? 1)),
  set: (value: number) => updateAllGoodsNumber('Preview_H', Math.max(1, value)),
})
const sharedGoodsStatusX = computed({
  get: () => Number(selectedGoods.value?.BuyBtn.X ?? 0),
  set: (value: number) => updateAllGoodsBuyButtonPosition('X', value),
})
const sharedGoodsStatusY = computed({
  get: () => Number(selectedGoods.value?.BuyBtn.Y ?? 0),
  set: (value: number) => updateAllGoodsBuyButtonPosition('Y', value),
})
const sharedGoodsBuyButtonIndex = computed({
  get: () => Number(selectedGoods.value?.BuyBtn.Index ?? 0),
  set: (value: number) => updateAllGoodsBuyButtonIndex(value),
})

const goodsDialogSelectedGoods = computed(() => goodsDialogGoodsDrafts.value[goodsDialogSelectedIndex.value] ?? null)
const goodsDialogStoryLockUsages = computed<StoryLockUsageItem[]>(() => {
  const goods = goodsDialogSelectedGoods.value
  const project = workspace.project
  if (!goods || Number(goods.GoodsType) !== GoodsTypeEnum.Map || !project) return []
  const goodsId = Number(goods.GoodsId ?? 0)
  if (!Number.isFinite(goodsId) || goodsId <= 0) return []
  const usages: StoryLockUsageItem[] = []
  for (const story of project.Storys()) {
    const events = project.Events(story.Id)
    events.forEach((event, eventIndex) => {
      if (event.Code !== EventID.StoryLock) return
      const commodityId = Number(event.Parameter?.[0] ?? 0)
      if (commodityId !== goodsId) return
      usages.push({
        key: `${story.Id}-${eventIndex}`,
        order: usages.length + 1,
        storyName: `${formatStoryMapId(story.Id)} · ${story.Name || '未命名剧情'}`,
        position: `第 ${eventIndex + 1} 行`,
      })
    })
  }
  return usages
})
const goodsDialogSummaryItems = computed(() =>
  goodsDialogGoodsDrafts.value.map((item, index: number) => ({
    index,
    name: item.GoodsName?.trim() || `商品${index + 1}`,
    price: Number(item.GoodsPrice ?? 0),
    typeText: goodsTypeOptions.find((option) => option.value === Number(item.GoodsType ?? GoodsTypeEnum.Var))?.label ?? '未知',
  })),
)

function addPropertyDraft() {
  const goods = goodsDialogSelectedGoods.value
  if (!goods) return
  goods.PropertyList.push({
    PropertyId: 0,
    Operation: 0,
    Number: 0,
  })
}

function updateGoodsDialogType(value: string | number | boolean) {
  const goods = goodsDialogSelectedGoods.value
  if (!goods) return
  const nextType = Number(value ?? GoodsTypeEnum.Var)
  goods.GoodsType = Number.isFinite(nextType) ? nextType : GoodsTypeEnum.Var
  if (!canGoodsRepeat(goods)) {
    goods.CanBuyRepeat = false
  }
}

function removePropertyDraft(index: number) {
  const goods = goodsDialogSelectedGoods.value
  if (!goods) return
  goods.PropertyList.splice(index, 1)
}

function openDetailDialog() {
  detailDialogDraft.value = cloneDetailPageDraft(props.paymentSetting.GoodDetailPage)
  detailDialogOpen.value = true
}

function closeDetailDialog() {
  detailDialogOpen.value = false
  detailDialogDraft.value = null
  stopDetailPreviewInteraction()
}

function confirmDetailDialog() {
  if (!detailDialogDraft.value) return
  applyDetailPageDraft(props.paymentSetting.GoodDetailPage, detailDialogDraft.value)
  closeDetailDialog()
}

function updateDetailGraph(value: string) {
  if (!detailDialogDraft.value) return
  detailDialogDraft.value.DetailGraph.name = String(value)
}

function updateDetailPreviewBackGraph(value: string) {
  if (!detailDialogDraft.value) return
  detailDialogDraft.value.PreviewBackGraph.name = String(value)
}

const detailPreviewScale = computed(() => {
  const width = mallDesignWidth.value
  const height = mallDesignHeight.value
  if (width <= 0 || height <= 0) return 1
  const scale = Math.min(
    detailPreviewStageSize.value.width > 0 ? detailPreviewStageSize.value.width / width : 1,
    detailPreviewStageSize.value.height > 0 ? detailPreviewStageSize.value.height / height : 1,
  )
  return Number.isFinite(scale) && scale > 0 ? scale : 1
})

function beginDetailPreviewInteraction(moveHandler: (event: PointerEvent) => void, event: PointerEvent) {
  window.addEventListener('pointermove', moveHandler)
  window.addEventListener('pointerup', stopDetailPreviewInteraction)
  event.preventDefault()
}

function stopDetailPreviewInteraction() {
  detailPreviewDragState.value = null
  detailPreviewResizeState.value = null
  window.removeEventListener('pointermove', handleDetailPreviewMove)
  window.removeEventListener('pointermove', handleDetailPreviewResize)
  window.removeEventListener('pointerup', stopDetailPreviewInteraction)
}

function handleDetailPreviewMove(event: PointerEvent) {
  const state = detailPreviewDragState.value
  const draft = detailDialogDraft.value
  if (!state || !draft) return
  const dx = Math.round((event.clientX - state.startX) / detailPreviewScale.value)
  const dy = Math.round((event.clientY - state.startY) / detailPreviewScale.value)
  if (state.target === 'preview-back') {
    draft.PreviewBack_X = state.originX + dx
    draft.PreviewBack_Y = state.originY + dy
    return
  }
  if (state.target === 'preview') {
    draft.Preview_X = state.originX + dx
    draft.Preview_Y = state.originY + dy
    return
  }
  if (state.target === 'name') {
    draft.GoodName_X = state.originX + dx
    draft.GoodName_Y = state.originY + dy
    return
  }
  if (state.target === 'total') {
    draft.FlowerTotal_X = state.originX + dx
    draft.FlowerTotal_Y = state.originY + dy
    return
  }
  if (state.target === 'flower') {
    draft.Flower_X = state.originX + dx
    draft.Flower_Y = state.originY + dy
    return
  }
  if (state.target === 'price') {
    draft.GoodPrice_X = state.originX + dx
    draft.GoodPrice_Y = state.originY + dy
    return
  }
  if (state.target === 'count') {
    draft.BuyCount_X = state.originX + dx
    draft.BuyCount_Y = state.originY + dy
    return
  }
  if (state.target === 'intro') {
    draft.GoodIntroView.X = state.originX + dx
    draft.GoodIntroView.Y = state.originY + dy
    return
  }
  const button =
    state.target === 'ok'
      ? draft.Ok_btn
      : state.target === 'cancel'
        ? draft.Cancel_btn
        : state.target === 'add'
          ? draft.Add_btn
          : draft.Minus_btn
  button.X = state.originX + dx
  button.Y = state.originY + dy
}

function handleDetailPreviewResize(event: PointerEvent) {
  const state = detailPreviewResizeState.value
  const draft = detailDialogDraft.value
  if (!state || !draft) return
  const dx = Math.round((event.clientX - state.startX) / detailPreviewScale.value)
  const dy = Math.round((event.clientY - state.startY) / detailPreviewScale.value)
  if (state.target === 'preview-back') {
    draft.PreviewBack_W = Math.max(1, state.originW + dx)
    draft.PreviewBack_H = Math.max(1, state.originH + dy)
    return
  }
  if (state.target === 'preview') {
    draft.Preview_W = Math.max(1, state.originW + dx)
    draft.Preview_H = Math.max(1, state.originH + dy)
    return
  }
  draft.GoodIntroView.Width = Math.max(1, state.originW + dx)
  draft.GoodIntroView.Height = Math.max(1, state.originH + dy)
}

function startDetailPreviewDrag(target: DetailDragTarget, event: PointerEvent) {
  const draft = detailDialogDraft.value
  if (!draft) return
  const source =
    target === 'preview-back'
      ? { X: draft.PreviewBack_X, Y: draft.PreviewBack_Y }
      : target === 'preview'
        ? { X: draft.Preview_X, Y: draft.Preview_Y }
      : target === 'name'
        ? { X: draft.GoodName_X, Y: draft.GoodName_Y }
      : target === 'total'
        ? { X: draft.FlowerTotal_X, Y: draft.FlowerTotal_Y }
      : target === 'flower'
        ? { X: draft.Flower_X, Y: draft.Flower_Y }
      : target === 'price'
        ? { X: draft.GoodPrice_X, Y: draft.GoodPrice_Y }
      : target === 'count'
        ? { X: draft.BuyCount_X, Y: draft.BuyCount_Y }
      : target === 'intro'
        ? { X: draft.GoodIntroView.X, Y: draft.GoodIntroView.Y }
      : target === 'ok'
        ? draft.Ok_btn
      : target === 'cancel'
        ? draft.Cancel_btn
      : target === 'add'
        ? draft.Add_btn
        : draft.Minus_btn
  detailPreviewDragState.value = {
    target,
    startX: event.clientX,
    startY: event.clientY,
    originX: Number(source.X ?? 0),
    originY: Number(source.Y ?? 0),
  }
  beginDetailPreviewInteraction(handleDetailPreviewMove, event)
}

function startDetailPreviewResize(target: DetailResizeTarget, event: PointerEvent) {
  const draft = detailDialogDraft.value
  if (!draft) return
  const source =
    target === 'preview-back'
      ? { Width: draft.PreviewBack_W, Height: draft.PreviewBack_H }
      : target === 'preview'
        ? { Width: draft.Preview_W, Height: draft.Preview_H }
        : { Width: draft.GoodIntroView.Width, Height: draft.GoodIntroView.Height }
  detailPreviewResizeState.value = {
    target,
    startX: event.clientX,
    startY: event.clientY,
    originW: Number(source.Width ?? 0),
    originH: Number(source.Height ?? 0),
  }
  beginDetailPreviewInteraction(handleDetailPreviewResize, event)
}

function handleDetailStageResize(size: { width: number; height: number }) {
  detailPreviewStageSize.value = size
}

const detailPreviewBackgroundUrl = computed(() => resolveGraphicUrl(detailDialogDraft.value?.DetailGraph?.name ?? ''))
const detailPreviewBackUrl = computed(() => resolveGraphicUrl(detailDialogDraft.value?.PreviewBackGraph?.name ?? ''))
const detailOkButtonUrl = computed(() => resolveImageOptionUrl(Number(detailDialogDraft.value?.Ok_btn?.Index ?? 0)))
const detailCancelButtonUrl = computed(() => resolveImageOptionUrl(Number(detailDialogDraft.value?.Cancel_btn?.Index ?? 0)))
const detailAddButtonUrl = computed(() => resolveImageOptionUrl(Number(detailDialogDraft.value?.Add_btn?.Index ?? 0)))
const detailMinusButtonUrl = computed(() => resolveImageOptionUrl(Number(detailDialogDraft.value?.Minus_btn?.Index ?? 0)))

const detailSelectedGoods = computed(() => selectedGoods.value)

const detailPreviewItems = computed<PreviewDrawItem[]>(() => {
  const draft = detailDialogDraft.value
  const goods = detailSelectedGoods.value
  if (!draft || !goods) return []
  const previewBackStyle = makeRectStyle(Number(draft.PreviewBack_X ?? 0), Number(draft.PreviewBack_Y ?? 0), Math.max(1, Number(draft.PreviewBack_W ?? 1)), Math.max(1, Number(draft.PreviewBack_H ?? 1)))
  const previewStyle = makeRectStyle(Number(draft.Preview_X ?? 0), Number(draft.Preview_Y ?? 0), Math.max(1, Number(draft.Preview_W ?? 1)), Math.max(1, Number(draft.Preview_H ?? 1)))
  const flowerStyle = makeRectStyle(Number(draft.Flower_X ?? 0), Number(draft.Flower_Y ?? 0), getImageSize(mallFlowerUrl.value, 18, 18).width, getImageSize(mallFlowerUrl.value, 18, 18).height)
  const okSize = getImageSize(detailOkButtonUrl.value, 72, 32)
  const cancelSize = getImageSize(detailCancelButtonUrl.value, 72, 32)
  const addSize = getImageSize(detailAddButtonUrl.value, 32, 32)
  const minusSize = getImageSize(detailMinusButtonUrl.value, 32, 32)
  return compactPreviewItems([
    makeImageItem('detail-preview-back', previewBackStyle, detailPreviewBackUrl.value, '预览图底板'),
    ...(draft.IsShowGoodImage ? [makeImageItem('detail-preview', previewStyle, resolveGraphicUrl(goods.Preview?.name ?? ''), '商品预览图')] : []),
    ...(draft.IsShowGoodName ? [{
      key: 'detail-name',
      kind: 'text' as const,
      x: Number(draft.GoodName_X ?? 0),
      y: Number(draft.GoodName_Y ?? 0),
      width: 180,
      height: 20,
      text: goods.GoodsName || '商品名称',
      fontSize: getMallFontSize(goods.GoodsFont, 21),
      fontFamily: getMallFontFamily(goods.GoodsFont),
      textColor: getMallFontColor(goods.GoodsFont, '#ffffff'),
    }] : []),
    {
      key: 'detail-total',
      kind: 'text' as const,
      x: Number(draft.FlowerTotal_X ?? 0),
      y: Number(draft.FlowerTotal_Y ?? 0),
      width: 140,
      height: 18,
      text: '总价',
      fontSize: 12,
      textColor: '#ffffff',
    },
    makeImageItem('detail-flower', flowerStyle, mallFlowerUrl.value, '鲜花', 18, 18),
    {
      key: 'detail-price',
      kind: 'text' as const,
      x: Number(draft.GoodPrice_X ?? 0),
      y: Number(draft.GoodPrice_Y ?? 0),
      width: 120,
      height: 18,
      text: String(Number(goods.GoodsPrice ?? 0)),
      fontSize: getMallFontSize(goods.GoodsPriceFont, 18),
      fontFamily: getMallFontFamily(goods.GoodsPriceFont),
      textColor: getMallFontColor(goods.GoodsPriceFont, '#fde68a'),
    },
    ...(draft.IsShowBuyCount ? [{
      key: 'detail-count',
      kind: 'text' as const,
      x: Number(draft.BuyCount_X ?? 0),
      y: Number(draft.BuyCount_Y ?? 0),
      width: 64,
      height: 18,
      text: String(Number(draft.Index ?? 1)),
      fontSize: 12,
      textColor: '#ffffff',
    }] : []),
    ...(draft.IsShowGoodIntro ? [{
      key: 'detail-intro',
      kind: 'text' as const,
      x: Number(draft.GoodIntroView?.X ?? 0),
      y: Number(draft.GoodIntroView?.Y ?? 0),
      width: Math.max(1, Number(draft.GoodIntroView?.Width ?? 1)),
      height: Math.max(1, Number(draft.GoodIntroView?.Height ?? 1)),
      text: goods.GoodsIntro || '商品简介',
      fontSize: getMallFontSize(goods.GoodsIntroFont, 18),
      fontFamily: getMallFontFamily(goods.GoodsIntroFont),
      textColor: getMallFontColor(goods.GoodsIntroFont, '#d1d5db'),
    }] : []),
    ...(detailOkButtonUrl.value ? [makeImageItem('detail-ok', makeRectStyle(Number(draft.Ok_btn?.X ?? 0), Number(draft.Ok_btn?.Y ?? 0), okSize.width, okSize.height), detailOkButtonUrl.value, '购买')] : []),
    ...(detailCancelButtonUrl.value ? [makeImageItem('detail-cancel', makeRectStyle(Number(draft.Cancel_btn?.X ?? 0), Number(draft.Cancel_btn?.Y ?? 0), cancelSize.width, cancelSize.height), detailCancelButtonUrl.value, '取消')] : []),
    ...(draft.IsShowBuyCount && detailAddButtonUrl.value ? [makeImageItem('detail-add', makeRectStyle(Number(draft.Add_btn?.X ?? 0), Number(draft.Add_btn?.Y ?? 0), addSize.width, addSize.height), detailAddButtonUrl.value, '加号', 32, 32)] : []),
    ...(draft.IsShowBuyCount && detailMinusButtonUrl.value ? [makeImageItem('detail-minus', makeRectStyle(Number(draft.Minus_btn?.X ?? 0), Number(draft.Minus_btn?.Y ?? 0), minusSize.width, minusSize.height), detailMinusButtonUrl.value, '减号', 32, 32)] : []),
  ])
})

const detailPreviewHotspots = computed<PreviewHotspotItem[]>(() => {
  const draft = detailDialogDraft.value
  if (!draft) return []
  const previewBackStyle = makeRectStyle(Number(draft.PreviewBack_X ?? 0), Number(draft.PreviewBack_Y ?? 0), Math.max(1, Number(draft.PreviewBack_W ?? 1)), Math.max(1, Number(draft.PreviewBack_H ?? 1)))
  const previewStyle = makeRectStyle(Number(draft.Preview_X ?? 0), Number(draft.Preview_Y ?? 0), Math.max(1, Number(draft.Preview_W ?? 1)), Math.max(1, Number(draft.Preview_H ?? 1)))
  const introStyle = makeRectStyle(Number(draft.GoodIntroView?.X ?? 0), Number(draft.GoodIntroView?.Y ?? 0), Math.max(1, Number(draft.GoodIntroView?.Width ?? 1)), Math.max(1, Number(draft.GoodIntroView?.Height ?? 1)))
  const flowerStyle = makeRectStyle(Number(draft.Flower_X ?? 0), Number(draft.Flower_Y ?? 0), getImageSize(mallFlowerUrl.value, 18, 18).width, getImageSize(mallFlowerUrl.value, 18, 18).height)
  const okSize = getImageSize(detailOkButtonUrl.value, 72, 32)
  const cancelSize = getImageSize(detailCancelButtonUrl.value, 72, 32)
  const addSize = getImageSize(detailAddButtonUrl.value, 32, 32)
  const minusSize = getImageSize(detailMinusButtonUrl.value, 32, 32)
  const items: PreviewHotspotItem[] = [
    {
      key: 'detail-preview-back',
      style: detailOutlineStyle('preview-back', previewBackStyle, '#f59e0b'),
      ariaLabel: '详情预览底板拖拽热区',
      onPointerdown: (event: PointerEvent) => startDetailPreviewDrag('preview-back', event),
      resizeHandle: {
        style: {
          position: 'absolute',
          right: '0',
          bottom: '0',
          width: '14px',
          height: '14px',
          borderLeft: '1px solid #fbbf24',
          borderTop: '1px solid #fbbf24',
          background: '#a16207',
          cursor: 'nwse-resize',
        },
        ariaLabel: '调整详情预览底板大小',
        onPointerdown: (event: PointerEvent) => startDetailPreviewResize('preview-back', event),
      },
    },
    {
      key: 'detail-preview',
      style: detailOutlineStyle('preview', previewStyle, '#38bdf8'),
      ariaLabel: '详情预览图拖拽热区',
      onPointerdown: (event: PointerEvent) => startDetailPreviewDrag('preview', event),
      resizeHandle: {
        style: {
          position: 'absolute',
          right: '0',
          bottom: '0',
          width: '14px',
          height: '14px',
          borderLeft: '1px solid #38bdf8',
          borderTop: '1px solid #38bdf8',
          background: '#0369a1',
          cursor: 'nwse-resize',
        },
        ariaLabel: '调整详情预览图大小',
        onPointerdown: (event: PointerEvent) => startDetailPreviewResize('preview', event),
      },
    },
    {
      key: 'detail-name',
      style: detailOutlineStyle('name', makeRectStyle(Number(draft.GoodName_X ?? 0), Number(draft.GoodName_Y ?? 0), 180, 20), '#22c55e'),
      ariaLabel: '详情名称拖拽热区',
      onPointerdown: (event: PointerEvent) => startDetailPreviewDrag('name', event),
    },
    {
      key: 'detail-total',
      style: detailOutlineStyle('total', makeRectStyle(Number(draft.FlowerTotal_X ?? 0), Number(draft.FlowerTotal_Y ?? 0), 140, 18), '#14b8a6'),
      ariaLabel: '详情总价拖拽热区',
      onPointerdown: (event: PointerEvent) => startDetailPreviewDrag('total', event),
    },
    {
      key: 'detail-flower',
      style: detailOutlineStyle('flower', flowerStyle, '#f97316'),
      ariaLabel: '详情鲜花图标拖拽热区',
      onPointerdown: (event: PointerEvent) => startDetailPreviewDrag('flower', event),
    },
    {
      key: 'detail-price',
      style: detailOutlineStyle('price', makeRectStyle(Number(draft.GoodPrice_X ?? 0), Number(draft.GoodPrice_Y ?? 0), 120, 18), '#eab308'),
      ariaLabel: '详情价格拖拽热区',
      onPointerdown: (event: PointerEvent) => startDetailPreviewDrag('price', event),
    },
    {
      key: 'detail-intro',
      style: detailOutlineStyle('intro', introStyle, '#ef4444'),
      ariaLabel: '详情简介拖拽热区',
      onPointerdown: (event: PointerEvent) => startDetailPreviewDrag('intro', event),
      resizeHandle: {
        style: {
          position: 'absolute',
          right: '0',
          bottom: '0',
          width: '14px',
          height: '14px',
          borderLeft: '1px solid #ef4444',
          borderTop: '1px solid #ef4444',
          background: '#991b1b',
          cursor: 'nwse-resize',
        },
        ariaLabel: '调整详情简介大小',
        onPointerdown: (event: PointerEvent) => startDetailPreviewResize('intro', event),
      },
    },
  ]
  if (detailOkButtonUrl.value) {
    items.push({
      key: 'detail-ok',
      style: detailOutlineStyle('ok', makeRectStyle(Number(draft.Ok_btn?.X ?? 0), Number(draft.Ok_btn?.Y ?? 0), okSize.width, okSize.height), '#3b82f6'),
      ariaLabel: '详情确认按钮拖拽热区',
      onPointerdown: (event: PointerEvent) => startDetailPreviewDrag('ok', event),
    })
  }
  if (detailCancelButtonUrl.value) {
    items.push({
      key: 'detail-cancel',
      style: detailOutlineStyle('cancel', makeRectStyle(Number(draft.Cancel_btn?.X ?? 0), Number(draft.Cancel_btn?.Y ?? 0), cancelSize.width, cancelSize.height), '#3b82f6'),
      ariaLabel: '详情取消按钮拖拽热区',
      onPointerdown: (event: PointerEvent) => startDetailPreviewDrag('cancel', event),
    })
  }
  if (draft.IsShowBuyCount) {
    items.push({
      key: 'detail-count',
      style: detailOutlineStyle('count', makeRectStyle(Number(draft.BuyCount_X ?? 0), Number(draft.BuyCount_Y ?? 0), 64, 18), '#a855f7'),
      ariaLabel: '详情购买数量拖拽热区',
      onPointerdown: (event: PointerEvent) => startDetailPreviewDrag('count', event),
    })
  }
  if (draft.IsShowBuyCount && detailAddButtonUrl.value) {
    items.push({
      key: 'detail-add',
      style: detailOutlineStyle('add', makeRectStyle(Number(draft.Add_btn?.X ?? 0), Number(draft.Add_btn?.Y ?? 0), addSize.width, addSize.height), '#3b82f6'),
      ariaLabel: '详情加号按钮拖拽热区',
      onPointerdown: (event: PointerEvent) => startDetailPreviewDrag('add', event),
    })
  }
  if (draft.IsShowBuyCount && detailMinusButtonUrl.value) {
    items.push({
      key: 'detail-minus',
      style: detailOutlineStyle('minus', makeRectStyle(Number(draft.Minus_btn?.X ?? 0), Number(draft.Minus_btn?.Y ?? 0), minusSize.width, minusSize.height), '#3b82f6'),
      ariaLabel: '详情减号按钮拖拽热区',
      onPointerdown: (event: PointerEvent) => startDetailPreviewDrag('minus', event),
    })
  }
  return items
})

onBeforeUnmount(() => {
  stopDetailPreviewInteraction()
})
</script>

<template>
  <div class="settings-page payment-page" :class="{ 'payment-page--portrait': portraitLayout }">
    <div class="payment-grid">
      <div class="payment-left-column">
        <section class="setting-card payment-left-card">
          <div class="card-title">商品列表</div>

          <button class="payment-action-button payment-action-button--secondary" type="button" @click="openDetailDialog">
            商品详情页设置
          </button>

          <div class="payment-left-actions">
            <label class="field-block">
              <span class="field-label">商品数量</span>
              <NumberStepperField
                :model-value="mallGoodsCountRef.value"
                class="cell-input sm"
                :min="1"
                :max="MAX_MALL_GOODS_COUNT"
                @update:model-value="mallGoodsCountRef.value = Number($event)"
              />
            </label>

            <button class="payment-action-button" type="button" :disabled="!selectedGoods" @click="openGoodsDialog">
              商品设置
            </button>
          </div>

          <div class="settings-item-list payment-goods-summary-list">
            <button
              v-for="item in goodsSummaryItems"
              :key="`mall-goods-summary-${item.index}`"
              type="button"
              class="payment-goods-summary-item"
              :class="{ 'payment-goods-summary-item--active': item.index === selectedGoodsIndex }"
              @click="selectPaymentPreviewTarget(item.index, 'goods-back')"
            >
              <span class="payment-goods-summary-order">{{ String(item.index + 1).padStart(2, '0') }}</span>
              <span class="payment-goods-summary-main">
                <span class="payment-goods-summary-name">{{ item.name }}</span>
                <span class="payment-goods-summary-meta">{{ item.typeText }} · {{ item.price }} 花</span>
              </span>
              <span
                class="payment-goods-summary-status"
                :class="{
                  'payment-goods-summary-status--up': item.statusText === '已上架',
                  'payment-goods-summary-status--down': item.statusText === '未上架',
                }"
              >
                {{ item.statusText }}
              </span>
            </button>
          </div>
        </section>
      </div>

      <div class="payment-center-column">
        <section class="setting-card payment-preview-card settings-preview-card">
          <CanvasPreviewSurface
            :style="mallPreviewHostStyle"
            host-class="settings-preview-shell payment-preview-shell payment-main-preview-shell"
            stage-class="settings-preview-stage payment-preview-stage"
            :background-url="mallBackgroundUrl"
            :design-width="mallDesignWidth"
            :design-height="mallDesignHeight"
            :zoomable="true"
            :zoom-step="0.1"
            background-empty-text="商城背景图"
            :draw-items="previewDrawItems"
            :hotspot-items="previewHotspotItems"
            :guide-active="previewGuideActive"
            @stage-resize="onPreviewStageResize"
          />
        </section>

        <section v-if="selectedGoods" class="setting-card payment-selected-card">
          <div class="card-title">当前商品布局</div>
          <div class="payment-scroll-block">
            <label class="field-block compact">
              <span class="field-label">商品底板位置</span>
              <div class="field-axis-grid field-axis-grid--plain">
                <NumberStepperField v-model="selectedGoods.GoodsBack_X" class="cell-input sm" :min="-9999" />
                <NumberStepperField v-model="selectedGoods.GoodsBack_Y" class="cell-input sm" :min="-9999" />
              </div>
            </label>

            <label class="field-block compact">
              <span class="field-label">商品底板宽高</span>
              <div class="field-axis-grid field-axis-grid--plain">
                <NumberStepperField v-model="selectedGoods.GoodsBack_W" class="cell-input sm" :min="1" />
                <NumberStepperField v-model="selectedGoods.GoodsBack_H" class="cell-input sm" :min="1" />
              </div>
            </label>

            <label class="field-block compact">
              <span class="field-label">商品名称位置</span>
              <div class="field-axis-grid field-axis-grid--plain">
                <NumberStepperField v-model="selectedGoods.GoodsName_X" class="cell-input sm" :min="-9999" />
                <NumberStepperField v-model="selectedGoods.GoodsName_Y" class="cell-input sm" :min="-9999" />
              </div>
            </label>

            <label class="field-block compact">
              <span class="field-label">商品鲜花位置</span>
              <div class="field-axis-grid field-axis-grid--plain">
                <NumberStepperField v-model="selectedGoods.Flower_X" class="cell-input sm" :min="-9999" />
                <NumberStepperField v-model="selectedGoods.Flower_Y" class="cell-input sm" :min="-9999" />
              </div>
            </label>

            <label class="field-block compact">
              <span class="field-label">商品价格位置</span>
              <div class="field-axis-grid field-axis-grid--plain">
                <NumberStepperField v-model="selectedGoods.GoodsPrice_X" class="cell-input sm" :min="-9999" />
                <NumberStepperField v-model="selectedGoods.GoodsPrice_Y" class="cell-input sm" :min="-9999" />
              </div>
            </label>

            <label class="field-block compact">
              <span class="field-label">商品简介位置</span>
              <div class="field-axis-grid field-axis-grid--plain">
                <NumberStepperField v-model="selectedGoods.GoodIntroView.X" class="cell-input sm" :min="-9999" />
                <NumberStepperField v-model="selectedGoods.GoodIntroView.Y" class="cell-input sm" :min="-9999" />
              </div>
            </label>

            <label class="field-block compact">
              <span class="field-label">商品简介宽高</span>
              <div class="field-axis-grid field-axis-grid--plain">
                <NumberStepperField v-model="selectedGoods.GoodIntroView.Width" class="cell-input sm" :min="1" />
                <NumberStepperField v-model="selectedGoods.GoodIntroView.Height" class="cell-input sm" :min="1" />
              </div>
            </label>

            <label class="field-block compact">
              <span class="field-label">商品图标</span>
              <ResourceSelectField
                :model-value="selectedGoods.Preview.name"
                category="graphic"
                :base-folder-path="graphicBaseFolderPath"
                display-mode="full"
                store-mode="relative"
                picker-title="选择商品图标"
                @update:model-value="selectedGoods.Preview.name = String($event)"
              />
            </label>

            <label class="field-block compact">
              <span class="field-label">商品图标位置</span>
              <div class="field-axis-grid field-axis-grid--plain">
                <NumberStepperField v-model="sharedGoodsPreviewX" class="cell-input sm" :min="-9999" />
                <NumberStepperField v-model="sharedGoodsPreviewY" class="cell-input sm" :min="-9999" />
              </div>
            </label>

            <label class="field-block compact">
              <span class="field-label">商品图标宽高</span>
              <div class="field-axis-grid field-axis-grid--plain">
                <NumberStepperField v-model="sharedGoodsPreviewW" class="cell-input sm" :min="1" />
                <NumberStepperField v-model="sharedGoodsPreviewH" class="cell-input sm" :min="1" />
              </div>
            </label>

            <label class="field-block compact">
              <span class="field-label">商品状态位置</span>
              <div class="field-axis-grid field-axis-grid--plain">
                <NumberStepperField v-model="sharedGoodsStatusX" class="cell-input sm" :min="-9999" />
                <NumberStepperField v-model="sharedGoodsStatusY" class="cell-input sm" :min="-9999" />
              </div>
            </label>
          </div>
        </section>
      </div>

      <div class="payment-right-column">
        <section class="setting-card payment-global-card">
          <div class="card-title">商城全局属性</div>
          <div class="payment-scroll-block">
            <label class="field-block compact">
              <span class="field-label">商城背景</span>
              <ResourceSelectField
                :model-value="paymentSetting.MallBack.name"
                category="graphic"
                :base-folder-path="graphicBaseFolderPath"
                display-mode="full"
                store-mode="relative"
                picker-title="选择商城背景"
                @update:model-value="paymentSetting.MallBack.name = String($event)"
              />
            </label>

            <label class="field-block compact">
              <span class="field-label">商城装饰</span>
              <ResourceSelectField
                :model-value="paymentSetting.DecorateBack.name"
                category="graphic"
                :base-folder-path="graphicBaseFolderPath"
                display-mode="full"
                store-mode="relative"
                picker-title="选择商城装饰"
                @update:model-value="paymentSetting.DecorateBack.name = String($event)"
              />
            </label>

            <div class="field-axis-grid field-axis-grid--plain">
              <NumberStepperField v-model="paymentSetting.Decorate_X" class="cell-input sm" :min="-9999" />
              <NumberStepperField v-model="paymentSetting.Decorate_Y" class="cell-input sm" :min="-9999" />
            </div>

            <label class="field-block compact">
              <span class="field-label">关闭按钮</span>
              <ImageOptionPickerField
                :model-value="paymentSetting.ClosePic.Index"
                :items="imageOptionPickerItems"
                :graphic-base-folder-path="imageOptionGraphicBaseFolderPath"
                title="选择关闭按钮"
                @update:model-value="paymentSetting.ClosePic.Index = Number($event)"
              />
            </label>

            <div class="field-axis-grid field-axis-grid--plain">
              <NumberStepperField v-model="paymentSetting.ClosePic.X" class="cell-input sm" :min="-9999" />
              <NumberStepperField v-model="paymentSetting.ClosePic.Y" class="cell-input sm" :min="-9999" />
            </div>

            <label class="field-block compact">
              <span class="field-label">商城底板</span>
              <ResourceSelectField
                :model-value="paymentSetting.GoodAreaBack.name"
                category="graphic"
                :base-folder-path="graphicBaseFolderPath"
                display-mode="full"
                store-mode="relative"
                picker-title="选择商城底板"
                @update:model-value="paymentSetting.GoodAreaBack.name = String($event)"
              />
            </label>

            <label class="field-block compact">
              <span class="field-label">商城底板位置</span>
              <div class="field-axis-grid field-axis-grid--plain">
                <NumberStepperField v-model="paymentSetting.GoodAreaBack_X" class="cell-input sm" :min="-9999" />
                <NumberStepperField v-model="paymentSetting.GoodAreaBack_Y" class="cell-input sm" :min="-9999" />
              </div>
            </label>

            <label class="field-block compact">
              <span class="field-label">商城底板宽高</span>
              <div class="field-axis-grid field-axis-grid--plain">
                <NumberStepperField v-model="paymentSetting.GoodAreaBack_W" class="cell-input sm" :min="1" />
                <NumberStepperField v-model="paymentSetting.GoodAreaBack_H" class="cell-input sm" :min="1" />
              </div>
            </label>

            <label v-if="selectedGoods" class="field-block compact">
              <span class="field-label">商品底板</span>
              <ResourceSelectField
                :model-value="selectedGoods.GoodBack.name"
                category="graphic"
                :base-folder-path="graphicBaseFolderPath"
                display-mode="full"
                store-mode="relative"
                picker-title="选择商品底板"
                @update:model-value="updateSelectedGoodsBack"
              />
            </label>

            <label v-if="selectedGoods" class="field-block compact">
              <span class="field-label">图标底图</span>
              <ResourceSelectField
                :model-value="selectedGoods.PreviewBack.name"
                category="graphic"
                :base-folder-path="graphicBaseFolderPath"
                display-mode="full"
                store-mode="relative"
                picker-title="选择图标底图"
                @update:model-value="updateAllGoodsPreviewBack"
              />
            </label>

            <label v-if="selectedGoods" class="field-block compact">
              <span class="field-label">图标底图位置</span>
              <div class="field-axis-grid field-axis-grid--plain">
                <NumberStepperField v-model="sharedGoodsPreviewBackX" class="cell-input sm" :min="-9999" />
                <NumberStepperField v-model="sharedGoodsPreviewBackY" class="cell-input sm" :min="-9999" />
              </div>
            </label>

            <label v-if="selectedGoods" class="field-block compact">
              <span class="field-label">图标底图宽高</span>
              <div class="field-axis-grid field-axis-grid--plain">
                <NumberStepperField v-model="sharedGoodsPreviewBackW" class="cell-input sm" :min="1" />
                <NumberStepperField v-model="sharedGoodsPreviewBackH" class="cell-input sm" :min="1" />
              </div>
            </label>

            <label v-if="selectedGoods" class="field-block compact">
              <span class="field-label">购买按钮</span>
              <ImageOptionPickerField
                :model-value="sharedGoodsBuyButtonIndex"
                :items="imageOptionPickerItems"
                :graphic-base-folder-path="imageOptionGraphicBaseFolderPath"
                title="选择购买按钮"
                @update:model-value="sharedGoodsBuyButtonIndex = Number($event)"
              />
            </label>

            <label v-if="selectedGoods" class="field-block compact">
              <span class="field-label">已购买</span>
              <ResourceSelectField
                :model-value="selectedGoods.BoughtBtn.name"
                category="graphic"
                :base-folder-path="graphicBaseFolderPath"
                display-mode="full"
                store-mode="relative"
                picker-title="选择已购买图片"
                @update:model-value="updateAllGoodsBoughtPic"
              />
            </label>

            <label v-if="selectedGoods" class="field-block compact">
              <span class="field-label">未上架</span>
              <ResourceSelectField
                :model-value="selectedGoods.NoUpPic.name"
                category="graphic"
                :base-folder-path="graphicBaseFolderPath"
                display-mode="full"
                store-mode="relative"
                picker-title="选择未上架图片"
                @update:model-value="updateAllGoodsNoUpPic"
              />
            </label>

            <label class="field-block compact">
              <span class="field-label">鲜花图片</span>
              <ResourceSelectField
                :model-value="paymentSetting.FlowerPic.name"
                category="graphic"
                :base-folder-path="graphicBaseFolderPath"
                display-mode="full"
                store-mode="relative"
                picker-title="选择鲜花图片"
                @update:model-value="paymentSetting.FlowerPic.name = String($event)"
              />
            </label>

            <div class="field-axis-grid field-axis-grid--plain">
              <NumberStepperField v-model="paymentSetting.Flower_X" class="cell-input sm" :min="-9999" />
              <NumberStepperField v-model="paymentSetting.Flower_Y" class="cell-input sm" :min="-9999" />
            </div>

            <label class="field-block compact">
              <span class="field-label">剩余鲜花位置</span>
              <div class="field-axis-grid field-axis-grid--plain">
                <NumberStepperField v-model="paymentSetting.FlowerOwn_X" class="cell-input sm" :min="-9999" />
                <NumberStepperField v-model="paymentSetting.FlowerOwn_Y" class="cell-input sm" :min="-9999" />
              </div>
            </label>

            <label class="field-block compact">
              <span class="field-label">鲜花数位置</span>
              <div class="field-axis-grid field-axis-grid--plain">
                <NumberStepperField v-model="paymentSetting.FlowerPrice_X" class="cell-input sm" :min="-9999" />
                <NumberStepperField v-model="paymentSetting.FlowerPrice_Y" class="cell-input sm" :min="-9999" />
              </div>
            </label>

            <label class="field-block compact">
              <span class="field-label">上一页按钮</span>
              <ImageOptionPickerField
                :model-value="paymentSetting.PrePic.Index"
                :items="imageOptionPickerItems"
                :graphic-base-folder-path="imageOptionGraphicBaseFolderPath"
                title="选择上一页按钮"
                @update:model-value="paymentSetting.PrePic.Index = Number($event)"
              />
            </label>

            <div class="field-axis-grid field-axis-grid--plain">
              <NumberStepperField v-model="paymentSetting.PrePic.X" class="cell-input sm" :min="-9999" />
              <NumberStepperField v-model="paymentSetting.PrePic.Y" class="cell-input sm" :min="-9999" />
            </div>

            <label class="field-block compact">
              <span class="field-label">下一页按钮</span>
              <ImageOptionPickerField
                :model-value="paymentSetting.NextPic.Index"
                :items="imageOptionPickerItems"
                :graphic-base-folder-path="imageOptionGraphicBaseFolderPath"
                title="选择下一页按钮"
                @update:model-value="paymentSetting.NextPic.Index = Number($event)"
              />
            </label>

            <div class="field-axis-grid field-axis-grid--plain">
              <NumberStepperField v-model="paymentSetting.NextPic.X" class="cell-input sm" :min="-9999" />
              <NumberStepperField v-model="paymentSetting.NextPic.Y" class="cell-input sm" :min="-9999" />
            </div>

            <label class="field-block compact">
              <span class="field-label">页码位置</span>
              <div class="field-axis-grid field-axis-grid--plain">
                <NumberStepperField v-model="paymentSetting.PageX" class="cell-input sm" :min="-9999" />
                <NumberStepperField v-model="paymentSetting.PageY" class="cell-input sm" :min="-9999" />
              </div>
            </label>

            <label class="payment-checkbox-row">
              <input v-model="paymentSetting.IsShowGoodName" type="checkbox" />
              <span>显示商品名称</span>
            </label>
            <label class="payment-checkbox-row">
              <input v-model="paymentSetting.IsShowGoodIntro" type="checkbox" />
              <span>显示商品简介</span>
            </label>
            <label class="payment-checkbox-row">
              <input v-model="paymentSetting.IsShowGoodImage" type="checkbox" />
              <span>显示商品图片</span>
            </label>
            <label class="payment-checkbox-row">
              <input v-model="paymentSetting.IsShowOneKeyGetBtn" type="checkbox" />
              <span>显示一键领取</span>
            </label>
          </div>
        </section>
      </div>
    </div>

    <div v-if="goodsDialogOpen" class="payment-dialog-overlay" @click.self="closeGoodsDialog">
      <section class="payment-dialog payment-goods-dialog">
        <header class="payment-dialog__header">
          <div class="payment-dialog__title">商品设置</div>
          <button type="button" class="payment-dialog__close" @click="closeGoodsDialog">×</button>
        </header>

        <div class="payment-dialog__body payment-dialog__body--goods">
          <section class="setting-card">
            <div class="card-title">商品列表</div>
            <div class="settings-item-list payment-goods-summary-list">
              <button
                v-for="item in goodsDialogSummaryItems"
                :key="`goods-dialog-${item.index}`"
                type="button"
                class="payment-goods-summary-item"
                :class="{ 'payment-goods-summary-item--active': item.index === goodsDialogSelectedIndex }"
                @click="goodsDialogSelectedIndex = item.index"
              >
                <span class="payment-goods-summary-order">{{ String(item.index + 1).padStart(2, '0') }}</span>
                <span class="payment-goods-summary-main">
                  <span class="payment-goods-summary-name">{{ item.name }}</span>
                  <span class="payment-goods-summary-meta">{{ item.typeText }} · {{ item.price }} 花</span>
                </span>
              </button>
            </div>
          </section>

          <section v-if="goodsDialogSelectedGoods" class="setting-card payment-dialog-form-card">
            <div class="payment-form-grid payment-form-grid--goods">
              <label class="field-block">
                <span class="field-label">商品名称</span>
                <input v-model="goodsDialogSelectedGoods.GoodsName" class="cell-input" type="text" />
              </label>

              <label class="field-block">
                <span class="field-label">商品类型</span>
                <AppSelectField
                  :model-value="goodsDialogSelectedGoods.GoodsType"
                  :options="goodsTypeOptions"
                  @update:model-value="updateGoodsDialogType"
                />
              </label>

              <label class="field-block">
                <span class="field-label">商品状态</span>
                <AppSelectField
                  v-model="goodsDialogSelectedGoods.Good_Status"
                  :options="GOODS_STATUS_OPTIONS"
                />
              </label>

              <label class="field-block">
                <span class="field-label">商品价格</span>
                <NumberStepperField v-model="goodsDialogSelectedGoods.GoodsPrice" class="cell-input sm" :min="0" />
              </label>

              <label class="field-block">
                <span class="field-label">丸子价格</span>
                <NumberStepperField v-model="goodsDialogSelectedGoods.GoodsPricePakoras" class="cell-input sm" :min="0" />
              </label>

              <label
                v-if="canGoodsRepeat(goodsDialogSelectedGoods)"
                class="payment-checkbox-row payment-checkbox-row--inline"
              >
                <input v-model="goodsDialogSelectedGoods.CanBuyRepeat" type="checkbox" />
                <span>重复购买</span>
              </label>

              <label class="field-block payment-form-grid__span">
                <span class="field-label">商品预览</span>
                <ResourceSelectField
                  :model-value="goodsDialogSelectedGoods.Preview.name"
                  category="graphic"
                  :base-folder-path="graphicBaseFolderPath"
                  display-mode="full"
                  store-mode="relative"
                  picker-title="选择商品预览"
                  @update:model-value="goodsDialogSelectedGoods.Preview.name = String($event)"
                />
              </label>

              <label class="field-block payment-form-grid__span">
                <span class="field-label">商品简介</span>
                <textarea v-model="goodsDialogSelectedGoods.GoodsIntro" class="cell-input payment-textarea" />
              </label>
            </div>

            <div v-if="Number(goodsDialogSelectedGoods.GoodsType) === GoodsTypeEnum.Var" class="payment-property-panel">
              <div class="payment-property-panel__header">
                <span class="field-label">数值属性列表</span>
                <button type="button" class="payment-action-button payment-action-button--small" @click="addPropertyDraft">
                  添加属性
                </button>
              </div>

              <div class="payment-property-list">
                <div v-if="goodsDialogSelectedGoods.PropertyList.length" class="payment-property-head">
                  <span>属性</span>
                  <span>操作</span>
                  <span>数值</span>
                  <span></span>
                </div>
                <div v-for="(property, index) in goodsDialogSelectedGoods.PropertyList" :key="`property-${index}`" class="payment-property-item">
                  <VariableIndexField
                    class="payment-property-variable"
                    :model-value="Number(property.PropertyId ?? 0)"
                    scope="var"
                    title="选择数值属性"
                    @update:model-value="property.PropertyId = Number($event)"
                  />
                  <AppSelectField
                    v-model="property.Operation"
                    :options="propertyOperationOptions"
                  />
                  <NumberStepperField v-model="property.Number" class="cell-input sm" :min="-999999999" />
                  <button type="button" class="payment-icon-button" @click="removePropertyDraft(Number(index))">删除</button>
                </div>
              </div>
            </div>

            <div v-if="goodsDialogStoryLockUsages.length" class="payment-story-lock-panel">
              <div class="field-label">剧情锁使用位置</div>
              <div class="payment-story-lock-table">
                <div class="payment-story-lock-row payment-story-lock-row--head">
                  <span>序号</span>
                  <span>所在剧情</span>
                  <span>位置</span>
                </div>
                <div
                  v-for="usage in goodsDialogStoryLockUsages"
                  :key="usage.key"
                  class="payment-story-lock-row"
                >
                  <span>{{ usage.order }}</span>
                  <span>{{ usage.storyName }}</span>
                  <span>{{ usage.position }}</span>
                </div>
              </div>
            </div>
          </section>
        </div>

        <footer class="payment-dialog__footer">
          <button type="button" class="payment-dialog-button payment-dialog-button--primary" @click="confirmGoodsDialog">确认</button>
          <button type="button" class="payment-dialog-button" @click="closeGoodsDialog">取消</button>
        </footer>
      </section>
    </div>

    <div v-if="detailDialogOpen && detailDialogDraft" class="payment-dialog-overlay" @click.self="closeDetailDialog">
      <section class="payment-dialog payment-detail-dialog">
        <header class="payment-dialog__header">
          <div class="payment-dialog__title">商品详情页</div>
          <button type="button" class="payment-dialog__close" @click="closeDetailDialog">×</button>
        </header>

        <div class="payment-dialog__body payment-dialog__body--detail">
          <section class="setting-card payment-detail-form-card">
            <div class="card-title">详情页属性</div>
            <div class="payment-scroll-block">
              <label class="field-block compact">
                <span class="field-label">详情页底板</span>
                <ResourceSelectField
                  :model-value="detailDialogDraft.DetailGraph.name"
                  category="graphic"
                  :base-folder-path="graphicBaseFolderPath"
                  display-mode="full"
                  store-mode="relative"
                  picker-title="选择详情页底板"
                  @update:model-value="updateDetailGraph"
                />
              </label>

              <label class="field-block compact">
                <span class="field-label">预览图底板</span>
                <ResourceSelectField
                  :model-value="detailDialogDraft.PreviewBackGraph.name"
                  category="graphic"
                  :base-folder-path="graphicBaseFolderPath"
                  display-mode="full"
                  store-mode="relative"
                  picker-title="选择预览图底板"
                  @update:model-value="updateDetailPreviewBackGraph"
                />
              </label>

              <label class="field-block compact">
                <span class="field-label">底板位置</span>
                <div class="field-axis-grid field-axis-grid--plain">
                  <NumberStepperField v-model="detailDialogDraft.PreviewBack_X" class="cell-input sm" :min="-9999" />
                  <NumberStepperField v-model="detailDialogDraft.PreviewBack_Y" class="cell-input sm" :min="-9999" />
                </div>
              </label>

              <label class="field-block compact">
                <span class="field-label">底板宽高</span>
                <div class="field-axis-grid field-axis-grid--plain">
                  <NumberStepperField v-model="detailDialogDraft.PreviewBack_W" class="cell-input sm" :min="1" />
                  <NumberStepperField v-model="detailDialogDraft.PreviewBack_H" class="cell-input sm" :min="1" />
                </div>
              </label>

              <label class="field-block compact">
                <span class="field-label">预览图位置</span>
                <div class="field-axis-grid field-axis-grid--plain">
                  <NumberStepperField v-model="detailDialogDraft.Preview_X" class="cell-input sm" :min="-9999" />
                  <NumberStepperField v-model="detailDialogDraft.Preview_Y" class="cell-input sm" :min="-9999" />
                </div>
              </label>

              <label class="field-block compact">
                <span class="field-label">预览图宽高</span>
                <div class="field-axis-grid field-axis-grid--plain">
                  <NumberStepperField v-model="detailDialogDraft.Preview_W" class="cell-input sm" :min="1" />
                  <NumberStepperField v-model="detailDialogDraft.Preview_H" class="cell-input sm" :min="1" />
                </div>
              </label>

              <label class="field-block compact">
                <span class="field-label">商品名称位置</span>
                <div class="field-axis-grid field-axis-grid--plain">
                  <NumberStepperField v-model="detailDialogDraft.GoodName_X" class="cell-input sm" :min="-9999" />
                  <NumberStepperField v-model="detailDialogDraft.GoodName_Y" class="cell-input sm" :min="-9999" />
                </div>
              </label>

              <label class="field-block compact">
                <span class="field-label">商品总价位置</span>
                <div class="field-axis-grid field-axis-grid--plain">
                  <NumberStepperField v-model="detailDialogDraft.FlowerTotal_X" class="cell-input sm" :min="-9999" />
                  <NumberStepperField v-model="detailDialogDraft.FlowerTotal_Y" class="cell-input sm" :min="-9999" />
                </div>
              </label>

              <label class="field-block compact">
                <span class="field-label">鲜花位置</span>
                <div class="field-axis-grid field-axis-grid--plain">
                  <NumberStepperField v-model="detailDialogDraft.Flower_X" class="cell-input sm" :min="-9999" />
                  <NumberStepperField v-model="detailDialogDraft.Flower_Y" class="cell-input sm" :min="-9999" />
                </div>
              </label>

              <label class="field-block compact">
                <span class="field-label">商品价格位置</span>
                <div class="field-axis-grid field-axis-grid--plain">
                  <NumberStepperField v-model="detailDialogDraft.GoodPrice_X" class="cell-input sm" :min="-9999" />
                  <NumberStepperField v-model="detailDialogDraft.GoodPrice_Y" class="cell-input sm" :min="-9999" />
                </div>
              </label>

              <label class="field-block compact">
                <span class="field-label">购买数量位置</span>
                <div class="field-axis-grid field-axis-grid--plain">
                  <NumberStepperField v-model="detailDialogDraft.BuyCount_X" class="cell-input sm" :min="-9999" />
                  <NumberStepperField v-model="detailDialogDraft.BuyCount_Y" class="cell-input sm" :min="-9999" />
                </div>
              </label>

              <label class="field-block compact">
                <span class="field-label">商品简介位置</span>
                <div class="field-axis-grid field-axis-grid--plain">
                  <NumberStepperField v-model="detailDialogDraft.GoodIntroView.X" class="cell-input sm" :min="-9999" />
                  <NumberStepperField v-model="detailDialogDraft.GoodIntroView.Y" class="cell-input sm" :min="-9999" />
                </div>
              </label>

              <label class="field-block compact">
                <span class="field-label">商品简介宽高</span>
                <div class="field-axis-grid field-axis-grid--plain">
                  <NumberStepperField v-model="detailDialogDraft.GoodIntroView.Width" class="cell-input sm" :min="1" />
                  <NumberStepperField v-model="detailDialogDraft.GoodIntroView.Height" class="cell-input sm" :min="1" />
                </div>
              </label>

              <label class="field-block compact">
                <span class="field-label">购买按钮</span>
                <ImageOptionPickerField
                  :model-value="detailDialogDraft.Ok_btn.Index"
                  :items="imageOptionPickerItems"
                  :graphic-base-folder-path="imageOptionGraphicBaseFolderPath"
                  title="选择购买按钮"
                  @update:model-value="detailDialogDraft.Ok_btn.Index = Number($event)"
                />
              </label>

              <div class="field-axis-grid field-axis-grid--plain">
                <NumberStepperField v-model="detailDialogDraft.Ok_btn.X" class="cell-input sm" :min="-9999" />
                <NumberStepperField v-model="detailDialogDraft.Ok_btn.Y" class="cell-input sm" :min="-9999" />
              </div>

              <label class="field-block compact">
                <span class="field-label">取消按钮</span>
                <ImageOptionPickerField
                  :model-value="detailDialogDraft.Cancel_btn.Index"
                  :items="imageOptionPickerItems"
                  :graphic-base-folder-path="imageOptionGraphicBaseFolderPath"
                  title="选择取消按钮"
                  @update:model-value="detailDialogDraft.Cancel_btn.Index = Number($event)"
                />
              </label>

              <div class="field-axis-grid field-axis-grid--plain">
                <NumberStepperField v-model="detailDialogDraft.Cancel_btn.X" class="cell-input sm" :min="-9999" />
                <NumberStepperField v-model="detailDialogDraft.Cancel_btn.Y" class="cell-input sm" :min="-9999" />
              </div>

              <label class="field-block compact">
                <span class="field-label">加号按钮</span>
                <ImageOptionPickerField
                  :model-value="detailDialogDraft.Add_btn.Index"
                  :items="imageOptionPickerItems"
                  :graphic-base-folder-path="imageOptionGraphicBaseFolderPath"
                  title="选择加号按钮"
                  @update:model-value="detailDialogDraft.Add_btn.Index = Number($event)"
                />
              </label>

              <div class="field-axis-grid field-axis-grid--plain">
                <NumberStepperField v-model="detailDialogDraft.Add_btn.X" class="cell-input sm" :min="-9999" />
                <NumberStepperField v-model="detailDialogDraft.Add_btn.Y" class="cell-input sm" :min="-9999" />
              </div>

              <label class="field-block compact">
                <span class="field-label">减号按钮</span>
                <ImageOptionPickerField
                  :model-value="detailDialogDraft.Minus_btn.Index"
                  :items="imageOptionPickerItems"
                  :graphic-base-folder-path="imageOptionGraphicBaseFolderPath"
                  title="选择减号按钮"
                  @update:model-value="detailDialogDraft.Minus_btn.Index = Number($event)"
                />
              </label>

              <div class="field-axis-grid field-axis-grid--plain">
                <NumberStepperField v-model="detailDialogDraft.Minus_btn.X" class="cell-input sm" :min="-9999" />
                <NumberStepperField v-model="detailDialogDraft.Minus_btn.Y" class="cell-input sm" :min="-9999" />
              </div>

              <label class="payment-checkbox-row">
                <input v-model="detailDialogDraft.IsShowBuyCount" type="checkbox" />
                <span>显示购买数量</span>
              </label>
              <label class="payment-checkbox-row">
                <input v-model="detailDialogDraft.IsShowGoodName" type="checkbox" />
                <span>显示商品名称</span>
              </label>
              <label class="payment-checkbox-row">
                <input v-model="detailDialogDraft.IsShowGoodIntro" type="checkbox" />
                <span>显示商品简介</span>
              </label>
              <label class="payment-checkbox-row">
                <input v-model="detailDialogDraft.IsShowGoodImage" type="checkbox" />
                <span>显示商品图片</span>
              </label>
            </div>
          </section>

          <section class="setting-card payment-detail-preview-card settings-preview-card">
            <div class="card-title">详情页预览</div>
            <CanvasPreviewSurface
              host-class="settings-preview-shell payment-preview-shell"
              stage-class="settings-preview-stage payment-preview-stage"
              :background-url="detailPreviewBackgroundUrl"
              :design-width="mallDesignWidth"
              :design-height="mallDesignHeight"
              background-empty-text="详情页底板"
              :draw-items="detailPreviewItems"
              :hotspot-items="detailPreviewHotspots"
              :guide-active="detailPreviewDragState !== null"
              @stage-resize="handleDetailStageResize"
            />
            <div class="field-hint">右侧预览可直接拖拽详情页的图层、文字和按钮，带角标的区域支持缩放。</div>
          </section>
        </div>

        <footer class="payment-dialog__footer">
          <button type="button" class="payment-dialog-button payment-dialog-button--primary" @click="confirmDetailDialog">确认</button>
          <button type="button" class="payment-dialog-button" @click="closeDetailDialog">取消</button>
        </footer>
      </section>
    </div>
  </div>
</template>

<style scoped>
@import './shared.css';

.settings-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  padding-top: 4px;
  box-sizing: border-box;
}

.payment-grid {
  display: grid;
  grid-template-columns: 180px minmax(0, 1fr) 240px;
  gap: 8px;
  height: 100%;
  min-height: 0;
}

.payment-left-column,
.payment-center-column,
.payment-right-column {
  display: flex;
  min-height: 0;
  flex-direction: column;
  gap: 8px;
}

.payment-center-column,
.payment-preview-card,
.payment-selected-card,
.payment-global-card,
.payment-left-card {
  min-height: 0;
}

.payment-center-column {
  overflow: hidden;
}

.payment-preview-card {
  flex: 0 0 auto;
}

.payment-page--portrait {
  padding: 0;
}

.payment-page--portrait .payment-grid {
  display: flex;
  flex-direction: column;
  gap: 0;
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.payment-page--portrait .payment-center-column {
  display: contents;
}

.payment-page--portrait .payment-preview-card {
  position: sticky;
  top: 0;
  z-index: 2;
  order: 1;
  flex: 0 0 var(--settings-portrait-preview-h);
  height: var(--settings-portrait-preview-h);
  min-height: 0;
  padding: 0;
  border: 0;
  background: var(--theme-bg-panel);
}

.payment-page--portrait .payment-preview-card :deep(.settings-preview-shell),
.payment-page--portrait .payment-preview-card :deep(.preview-surface),
.payment-page--portrait .payment-preview-card :deep(.preview-surface-scroll) {
  width: 100%;
  height: 100%;
  min-height: 0;
}

.payment-page--portrait .payment-left-column,
.payment-page--portrait .payment-selected-card,
.payment-page--portrait .payment-right-column {
  flex: 0 0 auto;
  margin: 8px;
}

.payment-page--portrait .payment-left-column {
  order: 2;
}

.payment-page--portrait .payment-selected-card {
  order: 3;
}

.payment-page--portrait .payment-right-column {
  order: 4;
}

.payment-selected-card {
  flex: 1 1 auto;
  overflow: hidden;
}

.payment-left-actions {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 8px;
}

.payment-action-button {
  height: 34px;
  padding: 0 12px;
  border: 1px solid var(--theme-accent);
  background: var(--theme-accent);
  color: #fff;
  cursor: pointer;
}

.payment-action-button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.payment-action-button--secondary {
  background: var(--theme-bg-card);
  color: var(--theme-text-heading);
  border-color: var(--theme-border);
}

.payment-action-button--small {
  height: 28px;
  padding: 0 10px;
}

.payment-scroll-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
  overflow-y: auto;
  padding-right: 2px;
}

.payment-goods-summary-list {
  margin-top: 4px;
}

.payment-goods-summary-item {
  display: grid;
  width: 100%;
  grid-template-columns: 28px minmax(0, 1fr) auto;
  gap: 8px;
  align-items: center;
  padding: 8px;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-raised);
  color: var(--theme-text-heading);
  text-align: left;
  cursor: pointer;
}

.payment-goods-summary-item--active {
  border-color: var(--theme-accent);
  background: color-mix(in srgb, var(--theme-accent) 12%, var(--theme-bg-raised));
}

.payment-goods-summary-order {
  font-size: 12px;
  color: var(--theme-text-secondary);
}

.payment-goods-summary-main {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
}

.payment-goods-summary-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.payment-goods-summary-meta {
  font-size: 11px;
  color: var(--theme-text-secondary);
}

.payment-goods-summary-status {
  font-size: 11px;
}

.payment-goods-summary-status--up {
  color: #f59e0b;
}

.payment-goods-summary-status--down {
  color: #94a3b8;
}

.payment-checkbox-row {
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 28px;
  font-size: 12px;
  color: var(--theme-text-secondary);
}

.payment-checkbox-row--span {
  grid-column: 1 / -1;
}

.payment-checkbox-row--inline {
  align-self: end;
  min-height: 30px;
}

.payment-dialog-overlay {
  position: fixed;
  inset: 0;
  z-index: 1600;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.35);
}

.payment-dialog {
  display: flex;
  width: min(1120px, calc(100vw - 48px));
  max-height: min(760px, calc(100vh - 48px));
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-panel);
}

.payment-detail-dialog {
  width: min(1220px, calc(100vw - 48px));
}

.payment-goods-dialog {
  height: min(640px, calc(100vh - 48px));
}

.payment-dialog__header,
.payment-dialog__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid var(--theme-border);
}

.payment-dialog__footer {
  justify-content: flex-end;
  border-top: 1px solid var(--theme-border);
  border-bottom: none;
}

.payment-dialog__title {
  font-size: 14px;
  font-weight: 600;
}

.payment-dialog__close {
  border: none;
  background: transparent;
  color: var(--theme-text-heading);
  font-size: 20px;
  cursor: pointer;
}

.payment-dialog__body {
  min-height: 0;
  padding: 12px;
  overflow: hidden;
}

.payment-dialog__body--goods {
  display: grid;
  grid-template-columns: 228px minmax(0, 1fr);
  flex: 1 1 auto;
  gap: 8px;
}

.payment-dialog__body--detail {
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr);
  gap: 8px;
}

.payment-dialog-form-card,
.payment-detail-form-card,
.payment-detail-preview-card {
  min-height: 0;
}

.payment-dialog__body--goods > .setting-card,
.payment-dialog-form-card {
  overflow: hidden;
}

.payment-dialog-form-card {
  overflow-y: auto;
}

.payment-detail-form-card {
  min-width: 0;
}

.payment-form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.payment-form-grid--goods {
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr) minmax(0, 1fr);
}

.payment-form-grid__span {
  grid-column: 1 / -1;
}

.payment-textarea {
  min-height: 88px;
  resize: vertical;
}

.payment-property-panel {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--theme-border);
}

.payment-property-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.payment-property-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  max-height: 178px;
  overflow-y: auto;
  padding-right: 2px;
}

.payment-property-head,
.payment-property-item {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 104px 164px 56px;
  gap: 8px;
  align-items: center;
  min-width: 0;
}

.payment-property-head {
  font-size: 11px;
  color: var(--theme-text-secondary);
}

.payment-property-variable {
  min-width: 0;
}

.payment-property-item > .cell-input,
.payment-property-item > .number-stepper-field,
.payment-property-item > .payment-icon-button {
  min-width: 0;
}

.payment-story-lock-panel {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--theme-border);
}

.payment-story-lock-table {
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: var(--theme-bg-card);
}

.payment-story-lock-row {
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr) 96px;
  gap: 8px;
  align-items: center;
  min-height: 28px;
  padding: 0 8px;
  border-bottom: 1px solid var(--theme-border);
  color: var(--theme-text-primary);
  font-size: 12px;
}

.payment-story-lock-row > span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.payment-story-lock-row--head {
  color: var(--theme-text-secondary);
  background: var(--theme-bg-raised);
  font-size: 11px;
}

.payment-story-lock-empty {
  padding: 8px;
  color: var(--theme-text-secondary);
  font-size: 12px;
}

.payment-icon-button {
  height: 30px;
  width: 56px;
  min-width: 56px;
  padding: 0;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-card);
  color: var(--theme-text-heading);
  cursor: pointer;
}

.payment-dialog-button {
  min-width: 92px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-card);
  color: var(--theme-text-heading);
  cursor: pointer;
}

.payment-dialog-button--primary {
  border-color: var(--theme-accent);
  background: var(--theme-accent);
  color: #fff;
}

.payment-preview-card :deep(.canvas-preview-surface__hotspot),
.payment-detail-preview-card :deep(.canvas-preview-surface__hotspot) {
  touch-action: none;
}

.payment-preview-shell {
  overflow: hidden;
}

.payment-main-preview-shell {
  flex: 0 0 auto;
  width: 100%;
  height: auto !important;
  aspect-ratio: var(--payment-preview-aspect, 16 / 9);
  align-self: center;
}

.payment-global-card :deep(.resource-select-input),
.payment-global-card :deep(.resource-select-trigger),
.payment-selected-card :deep(.resource-select-input),
.payment-selected-card :deep(.resource-select-trigger),
.payment-dialog :deep(.resource-select-input),
.payment-dialog :deep(.resource-select-trigger),
.payment-dialog :deep(.image-option-picker-input),
.payment-dialog :deep(.image-option-picker-trigger) {
  background: var(--theme-bg-card);
  border: 1px solid var(--theme-border);
  color: var(--theme-text-heading);
}

@media (max-width: 1180px) {
  .payment-dialog__body--goods {
    grid-template-columns: 210px minmax(0, 1fr);
  }

  .payment-dialog__body--detail {
    grid-template-columns: 260px minmax(0, 1fr);
  }

  .payment-form-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .payment-form-grid--goods {
    grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr) minmax(0, 1fr);
  }

  .payment-property-head,
  .payment-property-item {
    grid-template-columns: minmax(0, 1fr) 96px 148px 56px;
  }
}
</style>
